import { EChartsType } from "hd-echarts/echarts";

import type { FeatureFlag } from "hooks/useFeatureFlags";

import {
  ChartAxisType,
  IAllChartTypes,
  IAllProductTypes,
  IChartScreenshotSettings,
  IChartSettings,
  lineCoordinates
} from "models/chart";
import { EntityKind } from "models/entityKind";

import AcidGasDisposition from "components/icons/AcidGasDisposition";
import AcidGasShrinkage from "components/icons/AcidGasShrinkage";
import CasingPressure from "components/icons/CasingPressure";
import FFVRatioIcon from "components/icons/FFVRatioIcon";
import LiquidsShrinkage from "components/icons/LiquidsShrinkage";
import OilCond from "components/icons/OilCond";
import TubingPressure from "components/icons/TubingPressure";

import {
  C2,
  C3,
  C4,
  C5,
  CO2,
  Flared,
  Fuel,
  GPWater,
  GasEnergy,
  GasInlet,
  GasOutlet,
  GasSales,
  GasShrinkage,
  HeatingValue, // InNetworkInlet,
  // InNetworkOutlet,
  InletUtilization,
  NglMix,
  NglMix234,
  OilCondC5,
  OutOfNetworkInlet,
  PolymerInjection,
  RatioIcon,
  SalesLiquids,
  SalesOutlet,
  Vented
} from "../components/icons";
import AcidGasInjection from "../components/icons/AcidGasInjection";
import CO2Injection from "../components/icons/CO2Injection";
import FFV from "../components/icons/FFV";
import GasInjection from "../components/icons/GasInjection";
import ProductBoe6 from "../components/icons/Icon_ProductBoe6";
import ProductBoe20 from "../components/icons/Icon_ProductBoe20";
import ProductBoe30 from "../components/icons/Icon_ProductBoe30";
import SalesBoe6 from "../components/icons/Icon_SalesBoe6";
import SalesBoe20 from "../components/icons/Icon_SalesBoe20";
import SalesBoe30 from "../components/icons/Icon_SalesBoe30";
import OilCut from "../components/icons/OilCut";
import OnTimePercent from "../components/icons/OnTimePercent";
import ProductGas from "../components/icons/ProductGas";
import ProductWater from "../components/icons/ProductWater";
import RatioCGR from "../components/icons/RatioCGR";
import RatioCSOR from "../components/icons/RatioCSOR";
import RatioCWB from "../components/icons/RatioCWB";
import RatioGOR from "../components/icons/RatioGOR";
import RatioISOR from "../components/icons/RatioISOR";
import RatioIVRR from "../components/icons/RatioIVRR";
import RatioIWB from "../components/icons/RatioIWB";
import RatioOnePlusWOR from "../components/icons/RatioOnePlusWOR";
import RatioVRR from "../components/icons/RatioVRR";
import RatioWGR from "../components/icons/RatioWGR";
import RatioWOR from "../components/icons/RatioWOR";
import SolventInjection from "../components/icons/SolventInjection";
import SteamInjection from "../components/icons/SteamInjection";
import Sulphur from "../components/icons/Sulphur";
import TotalFluid from "../components/icons/TotalFluid";
import TotalGasInjection from "../components/icons/TotalGasInjection";
import TotalLiquidInjection from "../components/icons/TotalLiquidInjection";
import WaterCut from "../components/icons/WaterCut";
import WaterInjection from "../components/icons/WaterInjection";
import { IGroupBy } from "../models";
import { IChartResult } from "../models/model";
import { EVA_RESEARCH_NAME } from "./app.constants";
import { CalculationMethod, EvaChart, EvaSource, TimeStep } from "./charts.enums";
import { FIELD_NAMES } from "./fieldLabels.constants";

export const DEFAULT_TITLE_FONT_SIZE = 18;
export const DEFAULT_FONT_SIZE = 14;
export const DEFAULT_AXIS_FONT_SIZE = 15;
export const DEFAULT_AXIS_FONT_WEIGHT = 400;
export const DEFAULT_LEGEND_FONT_SIZE = 12;
export const DEFAULT_AXIS_LABEL_FONT_SIZE = 13;
export const DEFAULT_OPACITY = 1;
export const DEFAULT_ITEM_HEIGHT = 32;
export const DEFAULT_LEGEND_Y_POSITION = 50;
export const DEFAULT_LEGEND_X_POSITION_BUFFER = 20;
export const DEFAULT_LEGEND_WIDTH = 200;
export const DEFAULT_LEGEND_MIN_WIDTH = "140px";
export const HEADING_FONT_SIZE_INCREASE = 2;
export const DEFAULT_DOT_OPACITY = 1;
export const DEFAULT_POINT_SIZE = 10;
export const DEFAULT_AXIS_SPLIT_NUMBER = 9;
export const BLACK_HEX = "#000000";
export const DEFAULT_FONT_WEIGHT = 500;
export const CHART_DEFAULT_MIN_LOG_VALUE = 0.1;
export const CHART_DEFAULT_UNDEFINED_MIN_LOG_VALUE = 0.0001;
export const DEFAULT_MOVING_AVG_DAYS = 0;
// This is for the default line thickness of added lines, e.g line of best fit, p10 etc
export const DEFAULT_LINE_THICKNESS = 3;
export const DEFAULT_NORMALIZE_BY_FIELD = "Well_Design.HzLength_m";
export const DEFAULT_NORMALIZE_BY_DISPLAY_FIELD = FIELD_NAMES.HZ_LENGTH;
export const DEFAULT_NORMALIZE_BY_PER = 100;
export const DEFAULT_NORMALIZE_BY_HIGHER_SCALER = 1;
export const DEFAULT_NORMALIZE_BY_LOWER_SCALER = 1;
export const DEFAULT_NORMALIZE_BY_UNIT = "";
export const DEFAULT_NORMALIZATION_UNITS = { 0: "m", 1: "m", 2: "ft" };
export const DEFAULT_NORMALIZE_BY_THRESHOLD = 0;

export const DEFAULT_NORMALIZE_BY = {
  useNormalizeBy: false,
  field: DEFAULT_NORMALIZE_BY_FIELD,
  per: DEFAULT_NORMALIZE_BY_PER,
  displayName: DEFAULT_NORMALIZE_BY_DISPLAY_FIELD,
  unit: DEFAULT_NORMALIZE_BY_UNIT,
  defaultUnit: "", // Original unit from custom fields (org-defined/user-defined)
  useMultilinearNormalization: false,
  threshold: DEFAULT_NORMALIZE_BY_THRESHOLD,
  lowerScalar: DEFAULT_NORMALIZE_BY_LOWER_SCALER,
  higherScalar: DEFAULT_NORMALIZE_BY_HIGHER_SCALER,
  units: DEFAULT_NORMALIZATION_UNITS
};

export const DEFAULT_NORMALIZE_BY_SETTING = {
  field: DEFAULT_NORMALIZE_BY_FIELD,
  display: DEFAULT_NORMALIZE_BY_DISPLAY_FIELD,
  per: DEFAULT_NORMALIZE_BY_PER,
  unit: DEFAULT_NORMALIZE_BY_UNIT,
  defaultUnit: "", // Original unit from custom fields (org-defined/user-defined)
  useMultilinearNormalization: false,
  threshold: DEFAULT_NORMALIZE_BY_THRESHOLD,
  lowerScalar: DEFAULT_NORMALIZE_BY_LOWER_SCALER,
  higherScalar: DEFAULT_NORMALIZE_BY_HIGHER_SCALER,
  useNormalizeBy: false,
  units: DEFAULT_NORMALIZATION_UNITS
};
export const DEFAULT_CHART_PRODUCT = "Oil + Cond";
export const DEFAULT_MIDSTREAM_CHART_PRODUCT = "Gas Inlet";
export const AXIS_LINE_COLOUR = "#F2F2F2";
export const AXIS_SPLIT_NUMBER = 9;
export const DEFAULT_AXIS_LINE_COLOUR = "#F2F2F2";
export const DEFAULT_HIGHLIGHTED_SYMBOL_SIZE = 10;
export const SELECTABLE_Y_AXIS_POINTER_LABEL_MARGIN = -11;
export const DEFAULT_MATERIAL_BALANCE_LINE_THICKNESS = 2;
export const DEFAULT_MATERIAL_BALANCE_LINE_Z_INDEX = 11;
export const SERIES_COLLECTION_DATA_COUNT_THRESHOLD = 20000;
export const DEFAULT_LARGE_SERIES_THRESHOLD = 2000;
export const PROBIT_LARGE_SERIES_THRESHOLD = 10000;
export const BOX_PLOT_HIGHLIGHTED_BORDER_WIDTH = 3;
export const BOX_PLOT_DEFAULT_BORDER_WIDTH = 1;
export const BOX_PLOT_HIGHLIGHTED_Z_INDEX = 100;
export const DEFAULT_Z_INDEX = 2;
export const DEFAULT_CUTOFF_VALUE = 50;
export const DEFAULT_SHUT_IN_MONTHS = 3;
// if data count is greater than this, we will revert back to old chart behavior
export const PROGRESSIVE_DATA_COUNT_THRESHOLD = 40000;
export const DEFAULT_COLOR = "black";
export const JITTER_PLOT_LEGEND_ITEMS_SERIES_NAME = "Jitter Plot Legend Items"; // must match back end
export const DEFAULT_LINE_WIDTH = 2;
export const SPACING_PER_X_CATEGORY = 6;
export const DEFAULT_GRID_RIGHT = 20;
export const LAST_LABEL_PADDING = 10;
export const DEFAULT_BOX_PLOT_Z_INDEX = 1;
export const BOX_PLOT_LABEL_Z_INDEX = DEFAULT_BOX_PLOT_Z_INDEX + 4;
export const PROBIT_FIT_PATTERN =
  /Fit - P90:.*,P50:.*,Mean:.*,Swanson's Mean:.*,P10:.*,P10\/P90:.*/;
export const PROBIT_ZERO_REPLACEMENT = 0.0001;
export const DEFAULT_PRECISION = 2;

export const AXIS_TYPE: { [key: string]: ChartAxisType } = Object.freeze({
  x: "x",
  y: "y"
});

export const hasMidstream = (hasFeature: (feature: FeatureFlag) => boolean): boolean => {
  return hasFeature("Midstream Allocation Model");
};

// To change chart title and axis labels go to ProductType.cs in eva-chart-svc
export const ALL_PRODUCT_TYPES: IAllProductTypes = {
  // primary
  Oil: {
    key: "Oil",
    label: "Oil + Cond",
    id: 0,
    icon: <OilCond key="oil-cond" />,
    unit: "bbl",
    tooltip:
      "Sum of Oil and Condensate, roughly yielded at separator conditions, but stabilized to sales"
  },
  Gas: {
    key: "Gas",
    label: "Gas",
    id: 1,
    icon: <ProductGas key="product-gas" />,
    unit: "mcf",
    tooltip: "Gas, roughly at separator conditions"
  },
  Water: {
    key: "Water",
    label: "Water",
    id: 3,
    icon: <ProductWater key="product-water" />,
    unit: "bbl",
    tooltip: "Sum of produced load water and reservoir water"
  },
  TotalFluid: {
    key: "Total Fluid",
    label: "Total Fluid",
    id: 4,
    icon: <TotalFluid key="total-fluid" />,
    unit: "bbl",
    tooltip: "Sum of volume of all recovered liquids (oil, cond, and water)"
  },
  BOE: {
    key: "BOE",
    label: "BOE",
    id: 12,
    icon: <ProductBoe6 key="product-boe" />,
    unit: "boe",
    tooltip: "Barrel of Oil Equivalent (6:1) using produced (Oil + Cond) and produced Gas"
  },
  BOE20: {
    key: "BOE 20",
    label: "BOE 20",
    id: 97,
    icon: <ProductBoe20 key="product-boe-20" />,
    unit: "boe",
    tooltip:
      "Barrel of Oil Equivalent (20:1) using produced (Oil + Cond) and produced Gas"
  },
  BOE30: {
    key: "BOE 30",
    label: "BOE 30",
    id: 98,
    icon: <ProductBoe30 key="product-boe-30" />,
    unit: "boe",
    tooltip:
      "Barrel of Oil Equivalent (30:1) using produced (Oil + Cond) and produced Gas"
  },
  WaterInj: {
    key: "Water Inj",
    label: "Water Inj",
    id: 15,
    icon: <WaterInjection key="water-injection" />,
    unit: "bbl"
  },
  GasInj: {
    key: "Gas Inj",
    label: "Gas Inj",
    id: 14,
    icon: <GasInjection key="gas-injection" />,
    unit: "mcf"
  },
  SteamInj: {
    key: "Steam Inj",
    label: "Steam Inj",
    id: 13,
    icon: <SteamInjection key="steam-injection" />,
    unit: "bbl"
  },
  PolymerInj: {
    key: "Polymer Inj",
    label: "Polymer Inj",
    id: 20,
    icon: <PolymerInjection key="polymer-injection" />
  },
  CO_2Inj: {
    key: "CO2 Inj",
    label: "CO₂ Inj",
    id: 24,
    icon: <CO2Injection key="co2-injection" />,
    unit: "mcf"
  },
  AcidGasInj: {
    key: "Acid Gas Inj",
    label: "Acid Gas Inj",
    id: 25,
    icon: <AcidGasInjection key="acid-gas-injection" />,
    unit: "mcf"
  },
  SolventInj: {
    key: "Solvent Inj",
    label: "Solvent Inj",
    id: 26,
    icon: <SolventInjection key="solvent-injection" />,
    unit: "mcf"
  },
  TotalLiquidInj: {
    key: "Total Liquid Inj",
    label: "Total Liquid Inj",
    id: 27,
    icon: <TotalLiquidInjection key="total-liquid-injection" />,
    unit: "bbl",
    tooltip: "Sum of all injected liquids (water and polymer)"
  },
  TotalGasInj: {
    key: "Total Gas Inj",
    label: "Total Gas Inj",
    id: 28,
    icon: <TotalGasInjection key="total-gas-injection" />,
    unit: "mcf",
    tooltip: "Sum of all injected gas (gas, CO₂, acid gas, and solvent)"
  },
  // ratio
  CGR: {
    key: "CGR",
    label: "(O+C)GR",
    id: 6,
    lock: 1000,
    icon: <RatioCGR key="ratio-cgr" />,
    unit: "bbl/mmcf",
    tooltip: "(Oil + Cond) to Gas Ratio"
  },
  GOR: {
    key: "GOR",
    label: "GOR",
    id: 5,
    lock: 20000,
    icon: <RatioGOR key="ratio-gor" />,
    unit: "scf/bbl",
    tooltip: "Gas to (Oil + Cond) Ratio"
  },
  WGR: {
    key: "WGR",
    label: "WGR",
    id: 7,
    icon: <RatioWGR key="ratio-wgr" />,
    unit: "bbl/mmcf",
    tooltip: "Water to Gas Ratio"
  },
  WOR: {
    key: "WOR",
    label: "WOR",
    id: 18,
    icon: <RatioWOR key="ratio-wor" />,
    unit: "bbl/bbl",
    tooltip: "Water to (Oil + Cond) Ratio"
  },
  OnePlusWOR: {
    key: "1+WOR",
    label: "1+WOR",
    id: 19,
    icon: <RatioOnePlusWOR key="ratio-one-plus-wor" />,
    unit: "bbl/bbl",
    tooltip: "1 + Water to (Oil + Cond) Ratio"
  },
  ISOR: {
    key: "ISOR",
    label: "ISOR",
    id: 9,
    lock: 10,
    icon: <RatioISOR key="ratio-isor" />,
    unit: "bbl/bbl",
    tooltip: "Instantaneous Steam to Oil Ratio"
  },
  CSOR: {
    key: "CSOR",
    label: "CSOR",
    id: 10,
    lock: 10,
    icon: <RatioCSOR key="ratio-csor" />,
    unit: "bbl/bbl",
    tooltip: "Cumulative Steam to Oil Ratio"
  },
  IVRR: {
    key: "IVRR",
    label: "IVRR",
    id: 106,
    icon: <RatioIVRR key="ratio-ivrr" />,
    unit: "bbl/bbl",
    tooltip:
      "Instantaneous Voidage Replacement Ratio (Injected Water by Produced Oil and Water Ratio)"
  },
  VRR: {
    key: "VRR",
    label: "VRR",
    id: 107,
    icon: <RatioVRR key="ratio-vrr" />,
    unit: "bbl/bbl",
    tooltip:
      "Voidage Replacement Ratio (Cum Injected Water by Cum Produced Oil and Water Ratio)"
  },
  IWB: {
    key: "IWB",
    label: "IWB",
    id: 16,
    icon: <RatioIWB key="ratio-iwb" />,
    unit: "%",
    tooltip:
      "Instantaneous Water Balance: (Water Injection - Steam Injection) / (Steam Injection)"
  },
  CWB: {
    key: "CWB",
    label: "CWB",
    id: 17,
    icon: <RatioCWB key="ratio-cwb" />,
    unit: "%",
    tooltip:
      "Cumulative Water Balance: (Water Injection - Steam Injection) / (Steam Injection)"
  },
  // percent
  OilCut: {
    key: "Oil Cut",
    label: "Oil Cut",
    id: 8,
    icon: <OilCut key="oil-cut" />,
    unit: "%",
    tooltip: "(Oil + Cond) divided by Total Fluid"
  },
  WaterCut: {
    key: "Water Cut",
    label: "Water Cut",
    id: 11,
    icon: <WaterCut key="water-cut" />,
    unit: "%",
    tooltip: "Water divided by Total Fluid"
  },
  OnTime: {
    key: "OnTime",
    label: "On-Time",
    id: 20,
    icon: <OnTimePercent key="on-time" />,
    unit: "%",
    tooltip: "Producing Hours divided by Hours in month"
  },
  Sulphur: {
    id: 21,
    key: "Sulphur",
    label: "Sulphur",
    icon: <Sulphur key="sulphur" />,
    midstreamTooltip: "Solid Sulphur processed from Gas"
  },
  SulphurYieldsOutlet: {
    id: 22,
    key: "Sulphur Yields Sales",
    label: "Sulphur Yields Outlet",
    icon: (
      <RatioIcon
        key="sales-sulphur"
        numeratorIcon={<Sulphur />}
        denominatorIcon={<GasOutlet />}
      />
    ),
    midstreamTooltip: "Sulphur divided by Gas Outlet"
  },
  SulphurYieldsInlet: {
    id: 23,
    key: "Sulphur Yields Raw",
    label: "Sulphur Yields Inlet",
    icon: (
      <RatioIcon
        key="raw-sulphur"
        numeratorIcon={<Sulphur />}
        denominatorIcon={<GasInlet />}
      />
    ),
    midstreamTooltip: "Sulphur divided by Gas Inlet"
  },
  C2: {
    id: 32,
    key: "C2",
    label: "C₂",
    icon: <C2 key="c2" />,
    tooltip: "Includes both spec and mix",
    midstreamTooltip: "Sum of liquid C₂-SP and C₂-MX processed from Gas",
    unit: "bbl"
  },
  C2YieldsRaw: {
    id: 40,
    key: "C2 Yields Raw",
    label: "C₂ Yields Raw",
    icon: (
      <RatioIcon key="raw-c2" numeratorIcon={<C2 />} denominatorIcon={<ProductGas />} />
    ),
    tooltip: "C₂ yield relative to Raw Gas",
    midstreamTooltip: "Ethane divided by Wellhead Gas",
    unit: "bbl/mmcf"
  },
  C2YieldsSales: {
    id: 48,
    key: "C2 Yields Sales",
    label: "C₂ Yields Sales",
    icon: (
      <RatioIcon key="sales-c2" numeratorIcon={<C2 />} denominatorIcon={<GasSales />} />
    ),
    tooltip: "C₂ yield relative to Sales Gas",
    midstreamTooltip: "Ethane divided by Sales Gas",
    unit: "bbl/mmcf"
  },
  C3: {
    id: 33,
    key: "C3",
    label: "C₃",
    icon: <C3 key="c3" />,
    tooltip: "Includes both spec and mix",
    midstreamTooltip: "Sum of liquid C₃-SP and C₃-MX processed from Gas",
    unit: "bbl"
  },
  C3YieldsRaw: {
    id: 41,
    key: "C3 Yields Raw",
    label: "C₃ Yields Raw",
    icon: (
      <RatioIcon key="raw-c3" numeratorIcon={<C3 />} denominatorIcon={<ProductGas />} />
    ),
    tooltip: "C₃ yield relative to Raw Gas",
    midstreamTooltip: "Propane divided by Wellhead Gas",
    unit: "bbl/mmcf"
  },
  C3YieldsSales: {
    id: 49,
    key: "C3 Yields Sales",
    label: "C₃ Yields Sales",
    icon: (
      <RatioIcon key="sales-c3" numeratorIcon={<C3 />} denominatorIcon={<GasSales />} />
    ),
    tooltip: "C₃ yield relative to Sales Gas",
    midstreamTooltip: "Propane divided by Sales Gas",
    unit: "bbl/mmcf"
  },
  C4: {
    id: 34,
    key: "C4",
    label: "C₄",
    icon: <C4 key="c4" />,
    tooltip: "Includes both spec and mix",
    midstreamTooltip: "Sum of liquid C₄-SP and C₄-MX processed from Gas",
    unit: "bbl"
  },
  C4YieldsRaw: {
    id: 42,
    key: "C4 Yields Raw",
    label: "C₄ Yields Raw",
    icon: (
      <RatioIcon key="raw-c4" numeratorIcon={<C4 />} denominatorIcon={<ProductGas />} />
    ),
    tooltip: "C₄ yield relative to Raw Gas",
    midstreamTooltip: "Butane divided by Wellhead Gas",
    unit: "bbl/mmcf"
  },
  C4YieldsSales: {
    id: 50,
    key: "C4 Yields Sales",
    label: "C₄ Yields Sales",
    icon: (
      <RatioIcon key="sales-c4" numeratorIcon={<C4 />} denominatorIcon={<GasSales />} />
    ),
    tooltip: "C₄ yield relative to Sales Gas",
    midstreamTooltip: "Butane divided by Sales Gas",
    unit: "bbl/mmcf"
  },
  C5: {
    id: 35,
    key: "C5",
    label: "C₅₊",
    icon: <C5 key="c5" />,
    tooltip: "Includes both spec and mix",
    midstreamTooltip: "Sum of liquid C₅₊-SP and C₅₊-MX products processed from Gas",
    unit: "bbl"
  },
  C5YieldsRaw: {
    id: 43,
    key: "C5 Yields Raw",
    label: "C₅₊ Yields Raw",
    icon: (
      <RatioIcon key="raw-c5" numeratorIcon={<C5 />} denominatorIcon={<ProductGas />} />
    ),
    tooltip: "C₅₊ yield relative to Raw Gas",
    midstreamTooltip: "Pentane+ divided by Wellhead Gas",
    unit: "bbl/mmcf"
  },
  C5YieldsSales: {
    id: 51,
    key: "C5 Yields Sales",
    label: "C₅₊ Yields Sales",
    icon: (
      <RatioIcon key="sales-c5" numeratorIcon={<C5 />} denominatorIcon={<GasSales />} />
    ),
    tooltip: "C₅₊ yield relative to Sales Gas",
    midstreamTooltip: "Pentane+ divided by Sales Gas",
    unit: "bbl/mmcf"
  },
  LiquidsYieldsRaw: {
    id: 45,
    key: "Liquids Yields Raw",
    label: "Liquids Yields Raw",
    icon: (
      <RatioIcon
        key="raw-liquids"
        numeratorIcon={<SalesLiquids />}
        denominatorIcon={<ProductGas />}
      />
    ),
    tooltip: "Sales Liquids yield relative to Raw Gas",
    midstreamTooltip: "Liquids divided by Wellhead Gas",
    unit: "bbl/mmcf"
  },
  LiquidsYieldsSales: {
    id: 52,
    key: "Liquids Yields Sales",
    label: "Liquids Yields Sales",
    icon: (
      <RatioIcon
        key="sales-liquid-ngl-mix"
        numeratorIcon={<SalesLiquids />}
        denominatorIcon={<GasSales />}
      />
    ),
    tooltip: "Sales Liquids yield relative to Sales Gas",
    midstreamTooltip: "Liquids divided by Sales Gas",
    unit: "bbl/mmcf"
  },
  NGL_234: {
    id: 39,
    key: "NGL_234",
    label: "NGL₂₃₄",
    icon: <NglMix234 key="ngl-234" />,
    tooltip: "Sum of liquid C₂, C₃, and C₄ processed from Gas",
    midstreamTooltip: "Sum of liquid C₂, C₃, and C₄ processed from Gas",
    unit: "bbl"
  },
  NGLMix: {
    id: 38,
    key: "NGL Mix",
    label: "NGL Mix",
    icon: <NglMix key="ngl-mix" />,
    tooltip: "Sum of liquid C₂, C₃, C₄, and C₅₊ processed from Gas",
    midstreamTooltip: "Sum of liquid C₂, C₃, C₄, and C₅₊ processed from Gas",
    unit: "bbl"
  },
  NGLMixYieldsRaw: {
    id: 46,
    key: "NGL Mix Yields Raw",
    label: "NGL Mix Yields Raw",
    icon: (
      <RatioIcon
        key="raw-ngl-mix"
        numeratorIcon={<NglMix />}
        denominatorIcon={<ProductGas />}
      />
    ),
    tooltip: "NGL Mix yield relative to Raw Gas",
    midstreamTooltip: "NGL Mix divided by Wellhead Gas",
    unit: "bbl/mmcf"
  },
  NGLMixYieldsSales: {
    id: 54,
    key: "NGL Mix Yields Sales",
    label: "NGL Mix Yields Sales",
    icon: (
      <RatioIcon
        key="sales-ngl-mix"
        numeratorIcon={<NglMix />}
        denominatorIcon={<GasSales />}
      />
    ),
    tooltip: "NGL Mix yield relative to Sales Gas",
    midstreamTooltip: "NGL Mix divided by Sales Gas",
    unit: "bbl/mmcf"
  },
  NGL_234YieldsRaw: {
    id: 47,
    key: "NGL_234 Yields Raw",
    label: "NGL₂₃₄ Yields Raw",
    icon: (
      <RatioIcon
        key="raw-ngl-234"
        numeratorIcon={<NglMix234 />}
        denominatorIcon={<ProductGas />}
      />
    ),
    tooltip: "NGL₂₃₄ yield relative to Raw Gas",
    midstreamTooltip: "NGL₂₃₄ divided by Wellhead Gas",
    unit: "bbl/mmcf"
  },
  NGL_234YieldsSales: {
    id: 55,
    key: "NGL_234 Yields Sales",
    label: "NGL₂₃₄ Yields Sales",
    icon: (
      <RatioIcon
        key="sales-ngl-234"
        numeratorIcon={<NglMix234 />}
        denominatorIcon={<GasSales />}
      />
    ),
    tooltip: "NGL₂₃₄ yield relative to Sales Gas",
    midstreamTooltip: "NGL₂₃₄ divided by Sales Gas",
    unit: "bbl/mmcf"
  },
  OilCondC5: {
    id: 37,
    key: "Oil+Cond+C5",
    label: "Oil, Cond, C₅₊",
    icon: <OilCondC5 key="oil-cond-c5" />,
    tooltip: "Sum of Oil, Condensate, and Plant C₅₊",
    midstreamTooltip: "Oil, Condensate, and Plant C₅₊ combined",
    unit: "bbl"
  },
  OilCondC5YieldsRaw: {
    id: 44,
    key: "Oil+Cond+C5 Yields Raw",
    label: "Oil, Cond, C₅₊ Yields Raw",
    icon: (
      <RatioIcon
        key="raw-oil-cond-c5"
        numeratorIcon={<OilCondC5 />}
        denominatorIcon={<ProductGas />}
      />
    ),
    tooltip: "Oil, Cond, C₅₊ yield relative to Raw Gas",
    midstreamTooltip: "Oil, Cond, C₅₊ divided by Wellhead Gas",
    unit: "bbl/mmcf"
  },
  OilCondC5YieldsSales: {
    id: 53,
    key: "Oil+Cond+C5 Yields Sales",
    label: "Oil, Cond, C₅₊ Yields Sales",
    icon: (
      <RatioIcon
        key="sales-oil-cond-c5"
        numeratorIcon={<OilCondC5 />}
        denominatorIcon={<GasSales />}
      />
    ),
    tooltip: "Oil, Cond, C₅₊ yield relative to Sales Gas",
    midstreamTooltip: "Oil, Cond, C₅₊ divided by Sales Gas",
    unit: "bbl/mmcf"
  },
  SalesGas: {
    id: 30,
    key: "Sales Gas",
    label: "Sales Gas",
    icon: <GasSales key="sales-gas" />,
    tooltip: "Plant outlet gas, allocated to the well",
    midstreamTooltip: "Plant outlet gas that's at sales spec",
    unit: "mcf"
  },
  SalesLiquids: {
    id: 36,
    key: "Sales Liquids",
    label: "Sales Liquids",
    icon: <SalesLiquids key="yields-sales-liquids" />,
    tooltip: "Sum of liquid Oil, Condensate, C₂, C₃, C₄, and C₅₊ processed from Gas",
    midstreamTooltip: "Sum of C₂, C₃, C₄, stablized",
    unit: "bbl"
  },
  Shrinkage: {
    id: 31,
    key: "Shrinkage",
    label: "Shrinkage",
    icon: <GasShrinkage key="shrinkage" />,
    tooltip: "(1 - (Sales Gas / Raw Gas))",
    midstreamTooltip: "Shrinkage is (1 - (Sales Gas/Raw Gas))",
    unit: "%"
  },
  TotalShrinkageInlet: {
    id: 86,
    key: "Total Shrinkage Inlet",
    label: "Total Shrinkage Inlet",
    icon: (
      <RatioIcon
        key="sales-oil-cond-c5"
        numeratorIcon={<GasShrinkage />}
        denominatorIcon={<GasInlet />}
      />
    ),
    midstreamTooltip: "Total Gas difference from inlet to outlet divided by Gas Inlet"
  },
  TotalShrinkageOutlet: {
    id: 87,
    key: "Total Shrinkage Outlet",
    label: "Total Shrinkage Outlet",
    icon: (
      <RatioIcon
        key="sales-oil-cond-c5"
        numeratorIcon={<GasShrinkage />}
        denominatorIcon={<GasOutlet />}
      />
    ),
    midstreamTooltip: "Total Gas difference from inlet to outlet divided by Gas Outlet"
  },
  GasEnergy: {
    id: 56,
    key: "Gas Energy",
    label: "Gas Energy",
    icon: <GasEnergy key="gasEnergy" />,
    tooltip: "Total heat energy in Sales Gas",
    midstreamTooltip: "How much energy is stored in the Gas",
    unit: "mmbtu"
  },
  GasEnergyYieldsSales: {
    id: 57,
    key: "Gas Energy Yields Sales",
    label: "Heating Value",
    icon: <HeatingValue key="sales-gasEnergy" />,
    tooltip: "Combustion Energy per Volume of Sales Gas",
    midstreamTooltip: "Amount of energy from the combustion of Gas",
    unit: "Btu/scf"
  },
  SalesBOE: {
    id: 59,
    key: "Sales BOE",
    label: "Sales BOE",
    icon: <SalesBoe6 key="sales-boe" />,
    tooltip:
      "Barrel of Oil Equivalent (6:1) using Oil, Cond, C₂, C₃, C₄, C₅₊, and Sales Gas",
    midstreamTooltip: "Barrel of Oil Equivalent Leaving the Plant Ready for Sale",
    unit: "boe"
  },
  SalesBOE20: {
    id: 99,
    key: "Sales BOE 20",
    label: "Sales BOE 20",
    icon: <SalesBoe20 key="sales-boe-20" />,
    tooltip:
      "Barrel of Oil Equivalent (20:1) using Oil, Cond, C₂, C₃, C₄, C₅₊, and Sales Gas",
    midstreamTooltip: "Barrel of Oil Equivalent Leaving the Plant Ready for Sale",
    unit: "boe"
  },
  SalesBOE30: {
    id: 100,
    key: "Sales BOE 30",
    label: "Sales BOE 30",
    icon: <SalesBoe30 key="sales-boe-30" />,
    tooltip:
      "Barrel of Oil Equivalent (30:1) using Oil, Cond, C₂, C₃, C₄, C₅₊, and Sales Gas",
    midstreamTooltip: "Barrel of Oil Equivalent Leaving the Plant Ready for Sale",
    unit: "boe"
  },
  Hours: {
    id: 124,
    key: "Hours",
    label: "Hours",
    icon: null,
    tooltip: "Producing hours in the month"
  },
  GPWater: {
    id: 60,
    key: "GP Water",
    label: "GP Water",
    icon: <GPWater key="gp-water" />,
    midstreamTooltip: "Water condensed from Gas at Gas Plants"
  },
  GPWaterYieldsOutlet: {
    id: 61,
    key: "GP Water Yields Sales",
    label: "GP Water Yields Outlet",
    icon: (
      <RatioIcon
        key="sales-gp-water"
        numeratorIcon={<GPWater />}
        denominatorIcon={<GasOutlet />}
      />
    ),
    midstreamTooltip: "GP Water divided by the Gas Outlet"
  },
  GPWaterYieldsInlet: {
    id: 62,
    key: "GP Water Yields Raw",
    label: "GP Water Yields Inlet",
    icon: (
      <RatioIcon
        key="raw-gp-water"
        numeratorIcon={<GPWater />}
        denominatorIcon={<GasInlet />}
      />
    ),
    midstreamTooltip: "GP Water divided by the Gas Inlet"
  },
  CasingPressure: {
    id: 63,
    key: "Casing Pressure",
    label: "CSG Pressure",
    icon: <CasingPressure />,
    tooltip: "Pressure measured in the well’s casing"
  },
  TubingPressure: {
    id: 64,
    key: "Tubing Pressure",
    label: "TBG Pressure",
    icon: <TubingPressure />,
    tooltip: "Pressure measured in the tubing"
  },
  CO_2: {
    id: 65,
    key: "CO2",
    label: "CO₂",
    available: hasMidstream,
    icon: <CO2 key="co2" />,
    midstreamTooltip: "Estimated Carbon Dioxide equivalent emissions"
  },
  InletUtilization: {
    id: 66,
    key: "Inlet Utilization",
    label: "Inlet Utilization",
    icon: <InletUtilization key="inlet-utilization" />,
    midstreamTooltip: "Gas Inlet divided by licensed capacity"
  },
  CO_2YieldsOutlet: {
    id: 67,
    key: "CO2 Yields Sales",
    label: "CO₂ Yields Outlet",
    available: hasMidstream,
    icon: (
      <RatioIcon
        key="sales-co2"
        numeratorIcon={<CO2 />}
        denominatorIcon={<GasOutlet />}
      />
    ),
    midstreamTooltip: "CO₂ divided by the Gas Outlet"
  },
  CO_2YieldsInlet: {
    id: 68,
    key: "CO2 Yields Raw",
    label: "CO₂ Yields Inlet",
    available: hasMidstream,
    icon: (
      <RatioIcon key="raw-co2" numeratorIcon={<CO2 />} denominatorIcon={<GasInlet />} />
    ),
    midstreamTooltip: "CO₂ divided by the Gas Inlet"
  },
  LiquidsShrinkage: {
    id: 69,
    key: "Liquids Shrinkage",
    label: "Liquids Shrinkage",
    icon: <LiquidsShrinkage key="liquids-shrinkage" />,
    midstreamTooltip: "Reduction in Gas due to Liquids extraction"
  },
  AcidGasShrinkage: {
    id: 70,
    key: "Acid Gas Shrinkage",
    label: "Acid Gas Shrinkage",
    icon: <AcidGasShrinkage key="acid-gas-shrinkage" />,
    midstreamTooltip: "Reduction in Gas due to Sulphur or Acid Gas extraction"
  },
  AcidGasDisposition: {
    id: 71,
    key: "Acid Gas Disposition",
    label: "Acid Gas Disposition",
    icon: <AcidGasDisposition key="acid-gas-disposition" />,
    midstreamTooltip: "Acid Gas Disposition from the facility"
  },
  LiquidsShrinkageInletRate: {
    id: 72,
    key: "Liquids Shrinkage Inlet Rate",
    label: "Liquids Shrinkage Inlet Rate",
    icon: (
      <RatioIcon
        key="liquids-shrinkage-inlet-rate"
        numeratorIcon={<LiquidsShrinkage />}
        denominatorIcon={<GasInlet />}
      />
    ),
    midstreamTooltip: "Liquids Shrinkage divided by Gas Inlet"
  },
  AcidGasShrinkageInletRate: {
    id: 73,
    key: "Acid Gas Shrinkage Inlet Rate",
    label: "Acid Gas Shrinkage Inlet Rate",
    icon: (
      <RatioIcon
        key="acid-gas-shrinkage-inlet-rate"
        numeratorIcon={<AcidGasShrinkage />}
        denominatorIcon={<GasInlet />}
      />
    ),
    midstreamTooltip: "Acid Gas Shrinkage divided by Gas Inlet"
  },
  AcidGasDispositionInletRate: {
    id: 74,
    key: "Acid Gas Disposition Inlet Rate",
    label: "Acid Gas Disposition Inlet Rate",
    icon: (
      <RatioIcon
        key="acid-gas-disposition-inlet-rate"
        numeratorIcon={<AcidGasDisposition />}
        denominatorIcon={<GasInlet />}
      />
    ),
    midstreamTooltip: "Acid Gas Disposition divided by Gas Inlet"
  },
  LiquidsShrinkageOutletRate: {
    id: 75,
    key: "Liquids Shrinkage Outlet Rate",
    label: "Liquids Shrinkage Outlet Rate",
    icon: (
      <RatioIcon
        key="liquids-shrinkage-outlet-rate"
        numeratorIcon={<LiquidsShrinkage />}
        denominatorIcon={<GasOutlet />}
      />
    ),
    midstreamTooltip: "Liquids Shrinkage divided by Gas Outlet"
  },
  AcidGasShrinkageOutletRate: {
    id: 76,
    key: "Acid Gas Shrinkage Outlet Rate",
    label: "Acid Gas Shrinkage Outlet Rate",
    icon: (
      <RatioIcon
        key="acid-gas-shrinkage-outlet-rate"
        numeratorIcon={<AcidGasShrinkage />}
        denominatorIcon={<GasOutlet />}
      />
    ),
    midstreamTooltip: "Acid Gas Shrinkage divided by Gas Outlet"
  },
  AcidGasDispositionOutletRate: {
    id: 77,
    key: "Acid Gas Disposition Outlet Rate",
    label: "Acid Gas Disposition Outlet Rate",
    icon: (
      <RatioIcon
        key="acid-gas-disposition-outlet-rate"
        numeratorIcon={<AcidGasDisposition />}
        denominatorIcon={<GasOutlet />}
      />
    ),
    midstreamTooltip: "Acid Gas Disposition divided by Gas Outlet"
  },
  FuelInletRate: {
    id: 78,
    key: "Fuel Inlet Rate",
    label: "Fuel Inlet Rate",
    icon: (
      <RatioIcon
        key="fuel-inlet-rate"
        numeratorIcon={<Fuel />}
        denominatorIcon={<GasInlet />}
      />
    ),
    midstreamTooltip: "Fuel divided by Gas Inlet"
  },
  FuelOutletRate: {
    id: 79,
    key: "Fuel Outlet Rate",
    label: "Fuel Outlet Rate",
    icon: (
      <RatioIcon
        key="fuel-outlet-rate"
        numeratorIcon={<Fuel />}
        denominatorIcon={<GasOutlet />}
      />
    ),
    midstreamTooltip: "Fuel divided by Gas Outlet"
  },
  FlaredInletRate: {
    id: 80,
    key: "Flared Inlet Rate",
    label: "Flared Inlet Rate",
    icon: (
      <RatioIcon
        key="flared-inlet-rate"
        numeratorIcon={<Flared />}
        denominatorIcon={<GasInlet />}
      />
    ),
    midstreamTooltip: "Flared divided by Gas Inlet"
  },
  FlaredOutletRate: {
    id: 81,
    key: "Flared Outlet Rate",
    label: "Flared Outlet Rate",
    icon: (
      <RatioIcon
        key="flared-outlet-rate"
        numeratorIcon={<Flared />}
        denominatorIcon={<GasOutlet />}
      />
    ),
    midstreamTooltip: "Flared divided by Gas Outlet"
  },
  VentedInletRate: {
    id: 82,
    key: "Vented Inlet Rate",
    label: "Vented Inlet Rate",
    icon: (
      <RatioIcon
        key="vented-inlet-rate"
        numeratorIcon={<Vented />}
        denominatorIcon={<GasInlet />}
      />
    ),
    midstreamTooltip: "Vented divided by Gas Inlet"
  },
  VentedOutletRate: {
    id: 83,
    key: "Vented Outlet Rate",
    label: "Vented Outlet Rate",
    icon: (
      <RatioIcon
        key="vented-outlet-rate"
        numeratorIcon={<Vented />}
        denominatorIcon={<GasOutlet />}
      />
    ),
    midstreamTooltip: "Vented divided by Gas Outlet"
  },
  FFV: {
    id: 84,
    key: "FFV",
    label: "FFV",
    icon: <FFV key="ffv" />,
    midstreamTooltip: "Sum of the Fuel, Flare, and Vented Gas"
  },
  FFVInletRate: {
    id: 85,
    key: "FFV Inlet Rate",
    label: "FFV Inlet Rate",
    icon: <FFVRatioIcon key="ffv-inlet-rate" denominatorIcon={<GasInlet />} />,
    midstreamTooltip: "FFV divided by Gas Inlet"
  },
  FFVOutletRate: {
    id: 86,
    key: "FFV Outlet Rate",
    label: "FFV Outlet Rate",
    icon: <FFVRatioIcon key="ffv-outlet-rate" denominatorIcon={<GasOutlet />} />,
    midstreamTooltip: "FFV divided by Gas Outlet"
  },
  // InNetworkOutlet: {
  //   id: 89,
  //   key: "In-Network Outlet",
  //   label: "In-Network Outlet",
  //   available: hasMidstream,
  //   icon: <InNetworkOutlet key="in-network-outlet" />,
  //   midstreamTooltip: "Outlet to a facility within the royalty network"
  // },
  SalesOutlet: {
    id: 90,
    key: "Sales Outlet",
    label: "Sales Outlet",
    icon: <SalesOutlet key="sales-outlet" />,
    midstreamTooltip: "Outlet to a sales point outside the royalty network"
  },
  OutOfNetworkInlet: {
    id: 91,
    key: "Out-of-Network Inlet",
    label: "Out-of-Network Inlet",
    icon: <OutOfNetworkInlet key="out-of-network-inlet" />,
    midstreamTooltip: "Gas entering a facility from outside of the royalty network"
  },
  // InNetworkInlet: {
  //   id: 92,
  //   key: "In-Network Inlet",
  //   label: "In-Network Inlet",
  //   available: hasMidstream,
  //   icon: <InNetworkInlet key="in-network-inlet" />,
  //   midstreamTooltip: "Gas entering a facility from within the royalty network"
  // },
  GasOutlet: {
    id: 93,
    key: "Gas Outlet",
    label: "Gas Outlet",
    icon: <GasOutlet key="gas-outlet" />,
    midstreamTooltip: "All Gas leaving the facility"
  },
  GasInlet: {
    key: "Gas Inlet",
    label: "Gas Inlet",
    id: 94,
    icon: <GasInlet key="gas-inlet" />,
    midstreamTooltip: "All Gas entering the facility"
  },

  /*** midstream products **/
  H2S: {
    key: "H2S",
    label: "H2S",
    id: 102,
    icon: <ProductGas key="product-h2s" />,
    midstreamTooltip: "Total amount of produced Sulphur"
  },
  Fuel: {
    key: "Fuel",
    label: "Fuel",
    id: 103,
    icon: <Fuel key="product-fuel" />,
    midstreamTooltip: "Gas burned as fuel at the facility"
  },
  Flared: {
    key: "Flared",
    label: "Flared",
    id: 104,
    icon: <Flared key="product-flare" />,
    midstreamTooltip: "Gas burned via flaring at the facility"
  },
  Vented: {
    key: "Vented",
    label: "Vented",
    id: 105,
    icon: <Vented key="product-vent" />,
    midstreamTooltip: "Gas released to the atmosphere at the facility"
  },
  PlantShrinkage: {
    key: "Plant Shrinkage",
    label: "Plant Shrinkage",
    id: 105,
    icon: <GasShrinkage key="product-plant-shrinkage" />,
    midstreamTooltip: "Percentage of volume or product lost within plant operations"
  },
  C2YieldsInlet: {
    id: 108,
    key: "C2 Yields Raw",
    label: "C₂ Yields Inlet",
    icon: (
      <RatioIcon key="inlet-c2" numeratorIcon={<C2 />} denominatorIcon={<GasInlet />} />
    ),
    midstreamTooltip: "Ethane divided by Gas Inlet"
  },
  C2YieldsOutlet: {
    id: 109,
    key: "C2 Yields Sales",
    label: "C₂ Yields Outlet",
    icon: (
      <RatioIcon key="outlet-c2" numeratorIcon={<C2 />} denominatorIcon={<GasOutlet />} />
    ),
    midstreamTooltip: "Ethane divided by Gas Outlet"
  },
  C3YieldsInlet: {
    id: 110,
    key: "C3 Yields Raw",
    label: "C₃ Yields Inlet",
    icon: (
      <RatioIcon key="inlet-c3" numeratorIcon={<C3 />} denominatorIcon={<GasInlet />} />
    ),
    midstreamTooltip: "Propane divided by Gas Inlet"
  },
  C3YieldsOutlet: {
    id: 111,
    key: "C3 Yields Sales",
    label: "C₃ Yields Outlet",
    icon: (
      <RatioIcon key="outlet-c3" numeratorIcon={<C3 />} denominatorIcon={<GasOutlet />} />
    ),
    midstreamTooltip: "Propane divided by Gas Outlet"
  },
  C4YieldsInlet: {
    id: 112,
    key: "C4 Yields Raw",
    label: "C₄ Yields Inlet",
    icon: (
      <RatioIcon key="inlet-c4" numeratorIcon={<C4 />} denominatorIcon={<GasInlet />} />
    ),
    midstreamTooltip: "Butane divided by Gas Inlet"
  },
  C4YieldsOutlet: {
    id: 113,
    key: "C4 Yields Sales",
    label: "C₄ Yields Outlet",
    icon: (
      <RatioIcon key="outlet-c4" numeratorIcon={<C4 />} denominatorIcon={<GasOutlet />} />
    ),
    midstreamTooltip: "Butane divided by Gas Outlet"
  },
  C5YieldsInlet: {
    id: 114,
    key: "C5 Yields Raw",
    label: "C₅₊ Yields Inlet",
    icon: (
      <RatioIcon key="inlet-c5" numeratorIcon={<C5 />} denominatorIcon={<GasInlet />} />
    ),
    midstreamTooltip: "Pentane+ divided by Gas Inlet"
  },
  C5YieldsOutlet: {
    id: 115,
    key: "C5 Yields Sales",
    label: "C₅₊ Yields Outlet",
    icon: (
      <RatioIcon key="outlet-c5" numeratorIcon={<C5 />} denominatorIcon={<GasOutlet />} />
    ),
    midstreamTooltip: "Pentane+ divided by Gas Outlet"
  },
  LiquidsYieldsInlet: {
    id: 116,
    key: "Liquids Yields Raw",
    label: "Liquids Yields Inlet",
    icon: (
      <RatioIcon
        key="inlet-liquids"
        numeratorIcon={<SalesLiquids />}
        denominatorIcon={<GasInlet />}
      />
    ),
    midstreamTooltip: "Total Liquids divided by Inlet Gas"
  },
  LiquidsYieldsOutlet: {
    id: 117,
    key: "Liquids Yields Sales",
    label: "Liquids Yields Outlet",
    icon: (
      <RatioIcon
        key="outlet-liquids"
        numeratorIcon={<SalesLiquids />}
        denominatorIcon={<GasOutlet />}
      />
    ),
    midstreamTooltip: "Total Liquids divided by Outlet Gas"
  },
  NGLMixYieldsInlet: {
    id: 118,
    key: "NGL Mix Yields Raw",
    label: "NGL Mix Yields Inlet",
    icon: (
      <RatioIcon
        key="inlet-ngl-mix"
        numeratorIcon={<NglMix />}
        denominatorIcon={<GasInlet />}
      />
    ),
    midstreamTooltip: "NGL Mix divided by Gas Inlet"
  },
  NGLMixYieldsOutlet: {
    id: 119,
    key: "NGL Mix Yields Sales",
    label: "NGL Mix Yields Outlet",
    icon: (
      <RatioIcon
        key="outlet-ngl-mix"
        numeratorIcon={<NglMix />}
        denominatorIcon={<GasOutlet />}
      />
    ),
    midstreamTooltip: "NGL Mix divided by Gas Outlet"
  },
  NGL_234YieldsInlet: {
    id: 120,
    key: "NGL_234 Yields Raw",
    label: "NGL₂₃₄ Yields Inlet",
    icon: (
      <RatioIcon
        key="inlet-ngl-234"
        numeratorIcon={<NglMix234 />}
        denominatorIcon={<GasInlet />}
      />
    ),
    midstreamTooltip: "NGL₂₃₄ divided by Gas Inlet"
  },
  NGL_234YieldsOutlet: {
    id: 121,
    key: "NGL_234 Yields Sales",
    label: "NGL₂₃₄ Yields Outlet",
    icon: (
      <RatioIcon
        key="outlet-ngl-234"
        numeratorIcon={<NglMix234 />}
        denominatorIcon={<GasOutlet />}
      />
    ),
    midstreamTooltip: "NGL₂₃₄ divided by Gas Outlet"
  },
  OilCondC5YieldsInlet: {
    id: 122,
    key: "Oil+Cond+C5 Yields Raw",
    label: "Oil, Cond, C₅₊ Yields Inlet",
    icon: (
      <RatioIcon
        key="inlet-oil-cond-c5"
        numeratorIcon={<OilCondC5 />}
        denominatorIcon={<GasInlet />}
      />
    ),
    midstreamTooltip: "Oil, Cond, C₅₊ divided by Inlet Gas"
  },
  OilCondC5YieldsOutlet: {
    id: 123,
    key: "Oil+Cond+C5 Yields Sales",
    label: "Oil, Cond, C₅₊ Yields Outlet",
    icon: (
      <RatioIcon
        key="outlet-oil-cond-c5"
        numeratorIcon={<OilCondC5 />}
        denominatorIcon={<GasOutlet />}
      />
    ),
    midstreamTooltip: "Oil, Cond, C₅₊ divided by Outlet Gas"
  }
};

export const PRODUCT_Y_MAX = [
  ALL_PRODUCT_TYPES.Shrinkage.label,
  ALL_PRODUCT_TYPES.CWB.label,
  ALL_PRODUCT_TYPES.IWB.label
];

export type ChartTypeLabels =
  | "Rate Cum"
  | "Rate Time"
  | "Rate Date"
  | "Total Rate Date"
  | "Cum Time"
  | "Cross Plot"
  | "Probit"
  | "Box Plot"
  | "Stacked Bar"
  | "Horizontal Stacked Bar"
  | "Pie"
  | "Mosaic"
  | "Material Balance Time"
  | "Total Rate Cum"
  | "CAGR"
  | "Decline Rate"
  | "Trend Date"
  | "Well Contribution"
  | "RF vs PV"
  | "RF vs Time"
  | "Injector Count Date"
  | "Activity vs Time"
  | "Recent Activity"
  | "Well Status vs Time"
  | "Well Status Current";

export const ALL_CHART_TYPES: IAllChartTypes = {
  RateCum: { label: "Rate Cum", id: 1 },
  RateTime: { label: "Rate Time", id: 2 },
  RateDate: { label: "Rate Date", id: 3 },
  TotalRateDate: { label: "Total Rate Date", id: 4 },
  CumTime: { label: "Cum Time", id: 10 },
  CrossPlot: { label: "Cross Plot", id: 0, lock: { x: true, y: true } },
  Probit: { label: "Probit", id: 5, lock: { x: true, y: true } },
  BoxPlot: { label: "Box Plot", id: 6, lock: { x: false, y: true } },
  StackedBar: { label: "Stacked Bar", id: 8, lock: { x: false, y: true } },
  // id MUST stay 8 (StackedBar's) — chart-svc's ChartType enum has no entry
  // for this frontend-only rotation, and an unrecognized id fails server-side.
  // StackedBarMidstream reuses StackedBar's id the same way.
  StackedBarHorizontal: {
    label: "Horizontal Stacked Bar",
    id: 8,
    lock: { x: true, y: false }
  },
  Pie: { label: "Pie", id: 7, lock: { x: false, y: false } },
  Mosaic: { label: "Mosaic", id: 9, lock: { x: false, y: false } },
  MaterialBalanceTime: {
    label: "Material Balance Time",
    id: 11,
    lock: { x: true, y: true }
  },
  TotalRateCum: { label: "Total Rate Cum", id: 12 },
  CAGR: {
    label: "CAGR",
    id: 13,
    tooltip: "Compound Annual Growth Rate"
  },
  BaseDeclineRate: {
    label: "Decline Rate",
    id: 14,
    tooltip: "Base Decline Rate"
  },
  TrendDate: { label: "Trend Date", id: 15 },
  WellContribution: { label: "Well Contribution", id: 16 },
  RecoveryPVI: {
    label: "RF vs PV",
    id: 17,
    tooltip: "Recovery Factor versus Pore Volume Injected"
  },
  RecoveryTime: {
    label: "RF vs Time",
    id: 18,
    tooltip: "Aggregated Recovery Factor versus Time Since First Production"
  },
  InjectorTotal: {
    label: "Injector Count Date",
    id: 21,
    lock: { x: true, y: true },
    tooltip: "Cumulative Injector Count versus On Injection Date"
  },
  // Rig/Frac Charts widget only (EVA-76) — appended last so the positional
  // icon/label alignment of the Well/Facility selector lists is untouched.
  RigFracActivity: {
    label: "Activity vs Time",
    id: 19,
    lock: { x: true, y: true },
    tooltip: "Rig and frac crew activity over time"
  },
  RigFracRecentActivity: {
    label: "Recent Activity",
    id: 20,
    tooltip: "Rig and frac crew activity over the last 7 days of available data"
  },
  // Well Status widget only. Over Time carries y-axis locks (the count
  // axis) but no x locks — its x-axis is a category axis of server-computed time
  // buckets, and the window is driven by the time-scale selector. Current Well
  // Status has no `lock` at all, same omission as RigFracRecentActivity.
  WellStatusOverTime: {
    label: "Well Status vs Time",
    id: 22,
    lock: { x: false, y: true },
    tooltip: "Well count by status over time"
  },
  WellStatusCurrent: {
    label: "Well Status Current",
    id: 23,
    tooltip: "Well count by current status"
  }
};

export type ChartTypeT = {
  name: string;
  items: ChartTypeLabels[];
};

export const ChartType: ChartTypeT[] = [
  {
    name: "Average",
    items: [
      ALL_CHART_TYPES.RateCum.label,
      ALL_CHART_TYPES.RateTime.label,
      ALL_CHART_TYPES.CumTime.label
    ]
  },
  {
    name: "Aggregate",
    items: [
      ALL_CHART_TYPES.RateDate.label,
      ALL_CHART_TYPES.TotalRateDate.label,
      ALL_CHART_TYPES.TotalRateCum.label
    ]
  },
  {
    name: "",
    items: [
      ALL_CHART_TYPES.CrossPlot.label,
      ALL_CHART_TYPES.Probit.label,
      ALL_CHART_TYPES.BoxPlot.label,
      ALL_CHART_TYPES.StackedBar.label,
      ALL_CHART_TYPES.StackedBarHorizontal.label,
      ALL_CHART_TYPES.Pie.label,
      ALL_CHART_TYPES.Mosaic.label,
      ALL_CHART_TYPES.MaterialBalanceTime.label,
      ALL_CHART_TYPES.CAGR.label,
      ALL_CHART_TYPES.BaseDeclineRate.label,
      ALL_CHART_TYPES.TrendDate.label,
      ALL_CHART_TYPES.RecoveryPVI.label,
      ALL_CHART_TYPES.RecoveryTime.label,
      ALL_CHART_TYPES.InjectorTotal.label
    ]
  }
];

// EVA-10: chart types gated by org-level feature flags (default-on).
const CHART_TYPE_FEATURE_FLAGS: Partial<Record<ChartTypeLabels, FeatureFlag>> = {
  "Material Balance Time": "Material Balance Time Chart",
  "RF vs PV": "RF vs PV Chart",
  "RF vs Time": "RF vs Time Chart"
};

/**
 * Returns the `FeatureFlag` that gates a given chart-type label, if any.
 * Used by callers outside this module (e.g. `useChartTypeHandler`) that need
 * to check whether a persisted/current chart type is currently flag-disabled,
 * without reaching into the module-private `CHART_TYPE_FEATURE_FLAGS` map.
 */
export const getChartTypeFlag = (label: ChartTypeLabels): Maybe<FeatureFlag> =>
  CHART_TYPE_FEATURE_FLAGS[label];

/**
 * Filters chart-type menu groups down to the entries whose feature flag (if
 * any) is enabled for the org. Types without a flag mapping are always kept.
 */
export const filterChartTypeGroups = (
  groups: ChartTypeT[],
  hasFeature: (flag: FeatureFlag) => boolean
): ChartTypeT[] =>
  groups
    .map((group) => ({
      ...group,
      items: group.items.filter((label) => {
        const flag = getChartTypeFlag(label);
        return !flag || hasFeature(flag);
      })
    }))
    .filter((group) => group.items.length > 0);

export const rateDateCategories = [
  "Percent",
  "Ratio",
  "Yields Raw",
  "Yields Sales",
  "Heating Value",
  "Yields Inlet",
  "Yields Outlet",
  "Percent Inlet",
  "Percent Outlet",
  "Shrinkage"
];

export const yieldsAndRawCatagories = [
  "Yields Raw",
  "Yields Sales",
  "Yields Inlet",
  "Yields Outlet",
  "Percent Inlet",
  "Percent Outlet"
];

const getMidStreamAggregateItems = (hasFacility: boolean): ChartTypeLabels[] => {
  const items: ChartTypeLabels[] = [
    ALL_CHART_TYPES.RateDate.label,
    ALL_CHART_TYPES.TotalRateDate.label
  ];
  if (hasFacility) {
    items.push(ALL_CHART_TYPES.WellContribution.label);
  }
  return items;
};

export const getMidstreamChartTypes = (hasFacility: boolean): ChartTypeT[] => [
  {
    name: "Aggregate",
    items: getMidStreamAggregateItems(hasFacility)
  },
  {
    name: "",
    items: [
      ALL_CHART_TYPES.CrossPlot.label,
      ALL_CHART_TYPES.BoxPlot.label,
      ALL_CHART_TYPES.StackedBar.label,
      ALL_CHART_TYPES.Mosaic.label,
      ALL_CHART_TYPES.TrendDate.label
    ]
  }
];

export const CHART_TYPE_LABELS = Object.keys(ALL_CHART_TYPES).map(
  (key) => ALL_CHART_TYPES[key].label
);

// Scoped chart list for the Rig/Frac Charts widget (EVA-76). Deliberately NOT
// part of the Well/Facility `ChartType` groups — the widget's selector renders
// this list only.
export const RIG_FRAC_CHART_TYPES: ChartTypeT[] = [
  {
    name: "Activity",
    items: [
      ALL_CHART_TYPES.RigFracActivity.label,
      ALL_CHART_TYPES.RigFracRecentActivity.label
    ]
  }
];

// Scoped chart list for the Well Status widget. Same rationale as
// RIG_FRAC_CHART_TYPES: deliberately NOT part of the Well/Facility `ChartType`
// groups — the widget's own selector renders this list only.
export const WELL_STATUS_CHART_TYPES: ChartTypeT[] = [
  {
    name: "Well Status",
    items: [
      ALL_CHART_TYPES.WellStatusOverTime.label,
      ALL_CHART_TYPES.WellStatusCurrent.label
    ]
  }
];

export type LineStyleValue = "solid" | number[];

/**
 * Predefined line styles for multi-product charts.
 * Index 0 is always "solid" (assigned to the first product).
 * Subsequent indices are dash patterns [dash, gap, ...] for ECharts.
 */

// Long dash followed by dense dots (dash-dot-dense pattern)
const DASH_DOT_DENSE: number[] = [30, ...Array(27).fill(1)];

export const MULTI_PRODUCT_LINE_STYLES: LineStyleValue[] = [
  "solid",
  [1, 1],
  DASH_DOT_DENSE,
  [30, 5],
  [3, 3],
  [10, 3, 1, 3],
  [10, 5],
  [1, 3],
  [30, 3, 1, 3, 10, 3, 1, 3],
  [3, 5, 1, 1, 1, 1, 1, 1, 10, 5, 1, 3, 10, 5]
];

/**
 * Identifies the MPC product legend so the spacing/screenshot pipeline can
 * recognise it. The legend is drawn as an ECharts `graphic` group (a native
 * `legend` cannot render items decoupled from series in this echarts build),
 * tagged with this id. See mpcProductLegend.ts and applySpacingToChartOptions.
 */
export const MPC_PRODUCT_LEGEND_ID = "mpc-product-legend";

/** Neutral swatch colour for the monochrome MPC product legend (colour encodes bin, not product). */
export const MPC_PRODUCT_LEGEND_SWATCH_COLOR = "#666666";

/** Vertical space (px) reserved below the axes for the MPC product legend graphic. */
export const MPC_PRODUCT_LEGEND_HEIGHT = 24;

/**
 * Default colours for the new MPC "Override bin colours" feature.
 *
 * Values copied from the old MultiPhaseChart at:
 *   src/components/multiphase-chart/constants/shared.constants.ts  (colors + productStyles)
 *
 * Keys are the ALL_PRODUCT_TYPES .key value lowercased, plus an optional perf suffix
 * ("" | "-fcst" | "-backfit"), matching what getEffectiveSeriesColor resolves.
 *
 * NOTE: These values are intentionally copied (not imported) to decouple the new MPC
 * from the old MultiPhaseChart module. If the old chart's colours change, update here too.
 */
export const MULTI_PRODUCT_DEFAULT_COLORS: Record<string, string> = {
  // oil (Oil + Cond)
  oil: "#1c8017",
  "oil-fcst": "#1c8017",
  "oil-backfit": "#1c8017",
  // gas
  gas: "#9c1717",
  "gas-fcst": "#9c1717",
  "gas-backfit": "#9c1717",
  // water
  water: "#163494",
  "water-fcst": "#163494",
  "water-backfit": "#163494",
  // boe
  boe: "#ffa500",
  "boe-fcst": "#ffa500",
  "boe-backfit": "#ffa500",
  // boe 20 / boe 30 — darker steps down from the 6:1 orange, spaced far enough apart
  // that all three ratios still tell apart at a 2px stroke.
  "boe 20": "#d06400",
  "boe 20-fcst": "#d06400",
  "boe 20-backfit": "#d06400",
  "boe 30": "#7d3c00",
  "boe 30-fcst": "#7d3c00",
  "boe 30-backfit": "#7d3c00",
  // gor
  gor: "#2f4f4f",
  "gor-fcst": "#2f4f4f",
  "gor-backfit": "#2f4f4f",
  // wgr
  wgr: "#2e8b57",
  "wgr-fcst": "#2e8b57",
  "wgr-backfit": "#2e8b57",
  // total fluid
  "total fluid": "#29AAB1",
  "total fluid-fcst": "#29AAB1",
  "total fluid-backfit": "#29AAB1",
  // oil cut
  "oil cut": "#7fff00",
  "oil cut-fcst": "#7fff00",
  "oil cut-backfit": "#7fff00",
  // water cut
  "water cut": "#4169e1",
  "water cut-fcst": "#4169e1",
  "water cut-backfit": "#4169e1",
  // isor
  isor: "#174c80",
  "isor-fcst": "#174c80",
  "isor-backfit": "#174c80",
  // cgr
  cgr: "#c961fa",
  "cgr-fcst": "#c961fa",
  "cgr-backfit": "#c961fa",
  // csor
  csor: "#f08080",
  "csor-fcst": "#f08080",
  "csor-backfit": "#f08080",
  // cwb
  cwb: "#da70d6",
  "cwb-fcst": "#da70d6",
  "cwb-backfit": "#da70d6",
  // iwb
  iwb: "#d8bfd8",
  "iwb-fcst": "#d8bfd8",
  "iwb-backfit": "#d8bfd8",
  // gas inj
  "gas inj": "#eee8aa",
  "gas inj-fcst": "#eee8aa",
  "gas inj-backfit": "#eee8aa",
  // steam inj
  "steam inj": "#98fb98",
  "steam inj-fcst": "#98fb98",
  "steam inj-backfit": "#98fb98",
  // water inj
  "water inj": "#98fb98",
  "water inj-fcst": "#98fb98",
  "water inj-backfit": "#98fb98",
  // wor
  wor: "#ff00ff",
  "wor-fcst": "#ff00ff",
  "wor-backfit": "#ff00ff",
  // 1+wor (ALL_PRODUCT_TYPES.OnePlusWOR.key = "1+WOR" → lowercase "1+wor")
  "1+wor": "#0000ff",
  "1+wor-fcst": "#0000ff",
  "1+wor-backfit": "#0000ff",
  // c2 (ALL_PRODUCT_TYPES.C2.key = "C2" → lowercase "c2")
  c2: "#ff0000",
  "c2-fcst": "#ff0000",
  "c2-backfit": "#ff0000",
  // c3
  c3: "#ff00ff",
  "c3-fcst": "#ff00ff",
  "c3-backfit": "#ff00ff",
  // c4
  c4: "#0d2058",
  "c4-fcst": "#0d2058",
  "c4-backfit": "#0d2058",
  // c5
  c5: "#9362c6",
  "c5-fcst": "#9362c6",
  "c5-backfit": "#9362c6",
  // sales gas
  "sales gas": "#79efc0",
  "sales gas-fcst": "#79efc0",
  "sales gas-backfit": "#79efc0",
  // gas energy
  "gas energy": "#5c0aa4",
  "gas energy-fcst": "#5c0aa4",
  "gas energy-backfit": "#5c0aa4",
  // gas energy yields sales
  "gas energy yields sales": "#9eb00f",
  "gas energy yields sales-fcst": "#9eb00f",
  "gas energy yields sales-backfit": "#9eb00f",
  // shrinkage
  shrinkage: "#20b749",
  "shrinkage-fcst": "#20b749",
  "shrinkage-backfit": "#20b749",
  // sales liquids
  "sales liquids": "#c0d67b",
  "sales liquids-fcst": "#c0d67b",
  "sales liquids-backfit": "#c0d67b",
  // oil+cond+c5 (ALL_PRODUCT_TYPES.OilCondC5.key = "Oil+Cond+C5" → lowercase "oil+cond+c5")
  "oil+cond+c5": "#a0a099",
  "oil+cond+c5-fcst": "#a0a099",
  "oil+cond+c5-backfit": "#a0a099",
  // ngl_234 (ALL_PRODUCT_TYPES.NGL_234.key = "NGL_234" → lowercase "ngl_234")
  ngl_234: "#7706e7",
  "ngl_234-fcst": "#7706e7",
  "ngl_234-backfit": "#7706e7",
  // ngl mix
  "ngl mix": "#e06771",
  "ngl mix-fcst": "#e06771",
  "ngl mix-backfit": "#e06771",
  // sales boe
  "sales boe": "#ff7f50",
  "sales boe-fcst": "#ff7f50",
  "sales boe-backfit": "#ff7f50",
  // sales boe 20 / sales boe 30 — darker steps down from the sales 6:1 coral.
  "sales boe 20": "#c62828",
  "sales boe 20-fcst": "#c62828",
  "sales boe 20-backfit": "#c62828",
  "sales boe 30": "#7a1c1c",
  "sales boe 30-fcst": "#7a1c1c",
  "sales boe 30-backfit": "#7a1c1c",
  // oil+cond+c5 yields raw
  "oil+cond+c5 yields raw": "#33a936",
  "oil+cond+c5 yields raw-fcst": "#33a936",
  "oil+cond+c5 yields raw-backfit": "#33a936",
  // ngl_234 yields raw
  "ngl_234 yields raw": "#ffc891",
  "ngl_234 yields raw-fcst": "#ffc891",
  "ngl_234 yields raw-backfit": "#ffc891",
  // ngl mix yields raw
  "ngl mix yields raw": "#65567c",
  "ngl mix yields raw-fcst": "#65567c",
  "ngl mix yields raw-backfit": "#65567c",
  // liquids yields raw
  "liquids yields raw": "#82b992",
  "liquids yields raw-fcst": "#82b992",
  "liquids yields raw-backfit": "#82b992",
  // c2 yields raw
  "c2 yields raw": "#f6793e",
  "c2 yields raw-fcst": "#f6793e",
  "c2 yields raw-backfit": "#f6793e",
  // c3 yields raw
  "c3 yields raw": "#cd6f31",
  "c3 yields raw-fcst": "#cd6f31",
  "c3 yields raw-backfit": "#cd6f31",
  // c4 yields raw
  "c4 yields raw": "#aee071",
  "c4 yields raw-fcst": "#aee071",
  "c4 yields raw-backfit": "#aee071",
  // c5 yields raw
  "c5 yields raw": "#eede9a",
  "c5 yields raw-fcst": "#eede9a",
  "c5 yields raw-backfit": "#eede9a",
  // oil+cond+c5 yields sales
  "oil+cond+c5 yields sales": "#4e40ba",
  "oil+cond+c5 yields sales-fcst": "#4e40ba",
  "oil+cond+c5 yields sales-backfit": "#4e40ba",
  // ngl_234 yields sales
  "ngl_234 yields sales": "#c4ee85",
  "ngl_234 yields sales-fcst": "#c4ee85",
  "ngl_234 yields sales-backfit": "#c4ee85",
  // ngl mix yields sales
  "ngl mix yields sales": "#77c93d",
  "ngl mix yields sales-fcst": "#77c93d",
  "ngl mix yields sales-backfit": "#77c93d",
  // liquids yields sales
  "liquids yields sales": "#f340c7",
  "liquids yields sales-fcst": "#f340c7",
  "liquids yields sales-backfit": "#f340c7",
  // c2 yields sales
  "c2 yields sales": "#7078c4",
  "c2 yields sales-fcst": "#7078c4",
  "c2 yields sales-backfit": "#7078c4",
  // c3 yields sales
  "c3 yields sales": "#e2edfc",
  "c3 yields sales-fcst": "#e2edfc",
  "c3 yields sales-backfit": "#e2edfc",
  // c4 yields sales
  "c4 yields sales": "#df5acb",
  "c4 yields sales-fcst": "#df5acb",
  "c4 yields sales-backfit": "#df5acb",
  // c5 yields sales
  "c5 yields sales": "#d36c48",
  "c5 yields sales-fcst": "#d36c48",
  "c5 yields sales-backfit": "#d36c48",
  // on-time (ALL_PRODUCT_TYPES.OnTime.key = "OnTime" → lowercase "ontime")
  ontime: "#777c24",
  "ontime-fcst": "#777c24",
  "ontime-backfit": "#777c24",
  // polymer inj
  "polymer inj": "#68ef1d",
  "polymer inj-fcst": "#68ef1d",
  "polymer inj-backfit": "#68ef1d",
  // total liquid inj
  "total liquid inj": "#ab119f",
  "total liquid inj-fcst": "#ab119f",
  "total liquid inj-backfit": "#ab119f",
  // co2 inj (ALL_PRODUCT_TYPES.CO_2Inj.key = "CO2 Inj" → lowercase "co2 inj")
  "co2 inj": "#c30004",
  "co2 inj-fcst": "#c30004",
  "co2 inj-backfit": "#c30004",
  // acid gas inj
  "acid gas inj": "#ec5370",
  "acid gas inj-fcst": "#ec5370",
  "acid gas inj-backfit": "#ec5370",
  // solvent inj (backfit uses ngl_234 colour in old MPC — copy exactly)
  "solvent inj": "#32703e",
  "solvent inj-fcst": "#32703e",
  "solvent inj-backfit": "#7706e7",
  // total gas inj
  "total gas inj": "#97a013",
  "total gas inj-fcst": "#97a013",
  "total gas inj-backfit": "#97a013",
  // casing pressure (ALL_PRODUCT_TYPES.CasingPressure.key = "Casing Pressure"; old MPC key "csg pressure")
  "casing pressure": "#5d58c9",
  "casing pressure-fcst": "#5d58c9",
  "casing pressure-backfit": "#5d58c9",
  // tubing pressure (ALL_PRODUCT_TYPES.TubingPressure.key = "Tubing Pressure"; old MPC key "tbg pressure")
  "tubing pressure": "#d748bc",
  "tubing pressure-fcst": "#d748bc",
  "tubing pressure-backfit": "#d748bc"
};

export const MULTI_PRODUCT_COMPATIBLE_CHART_TYPES = [
  EvaChart.RateCum,
  EvaChart.RateDate,
  EvaChart.RateTime,
  EvaChart.CumTime,
  EvaChart.TotalRateCum
  // to be implemented in future
  // EvaChart.TotalRateDate
];

// In MPC mode, On-Time and Shrinkage cannot be plotted on cumulative-axis charts.
// Used by ProductSelector (button disabling) and useChartProductHandler (auto-removal
// when switching chart types) — keep the two behaviours in sync.
export const MPC_ON_TIME_SHRINKAGE_INCOMPATIBLE_CHART_TYPES = [
  EvaChart.RateCum,
  EvaChart.TotalRateCum,
  EvaChart.CumTime
];

export const ChartProductTypes = [
  ALL_CHART_TYPES.RateCum.label,
  ALL_CHART_TYPES.RateTime.label,
  ALL_CHART_TYPES.RateDate.label,
  ALL_CHART_TYPES.TotalRateDate.label,
  ALL_CHART_TYPES.CumTime.label,
  ALL_CHART_TYPES.MaterialBalanceTime.label,
  ALL_CHART_TYPES.TotalRateCum.label,
  ALL_CHART_TYPES.CAGR.label,
  ALL_CHART_TYPES.BaseDeclineRate.label,
  ALL_CHART_TYPES.RecoveryTime.label,
  // TODO consider unifying ALL_CHART_TYPES and EvaChart
  EvaChart.RateDateMidstream,
  EvaChart.TotalRateDateMidstream
];
export const IS_BOX_PLOT_CHART = (chartType: EvaChart) => {
  return chartType === EvaChart.BoxPlot || chartType === EvaChart.BoxPlotMidstream;
};
export const IS_CROSS_PLOT_CHART = (chartType: EvaChart) => {
  return chartType === EvaChart.CrossPlot || chartType === EvaChart.CrossPlotMidstream;
};
export const IS_MOSAIC_CHART = (chartType: EvaChart) => {
  return chartType === EvaChart.Mosaic || chartType === EvaChart.MosaicMidstream;
};
export const IS_STACKED_BAR_CHART = (chartType: EvaChart) => {
  return chartType === EvaChart.StackedBar || chartType === EvaChart.StackedBarMidstream;
};
// Deliberately NOT folded into IS_STACKED_BAR_CHART — its category/numeric
// fields sit on the opposite chartTypeParam axis (x=numeric, y=category), so
// axis-role-sensitive call sites must branch on this separately.
export const IS_STACKED_BAR_HORIZONTAL_CHART = (chartType: EvaChart) => {
  return chartType === EvaChart.StackedBarHorizontal;
};
/**
 * Screenshot-hook variant of IS_STACKED_BAR_HORIZONTAL_CHART: those hooks only
 * see the wire chartType (a number), and this chart type reuses StackedBar's
 * wire id (chart-svc has none of its own) — so the reliable signal is that its
 * translator is the only StackedBar-family member putting a category axis on y.
 *
 * Known gap: an empty chart's translator returns no yAxis at all, so this
 * returns false and callers fall back to treating it as vertical StackedBar
 * (wrong label rotation, screenshot defaults, telemetry label). Low impact
 * since the chart is empty either way; fixing it needs a request-side signal
 * threaded in, not just axis shape.
 */
export const IS_STACKED_BAR_HORIZONTAL_SCREENSHOT = (
  numericChartType: Maybe<number>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  yAxisOption: any
): boolean => {
  if (numericChartType !== ALL_CHART_TYPES.StackedBar.id) return false;
  const yAxis = Array.isArray(yAxisOption) ? yAxisOption[0] : yAxisOption;
  return yAxis?.type === "category";
};
export const IS_TREND_DATE_CHART = (chartType: EvaChart) => {
  return chartType === EvaChart.TrendDate || chartType === EvaChart.TrendDateMidstream;
};
// Single source of truth for "this chart type has no production/forecast concept" —
// keep the front-end data-table column exclusion and the Data Sources UI gate in sync.
// Mirrored server-side by IncludeWellSourceHeaders in eva-chart-svc.
export const CHART_HAS_WELL_SOURCES = (chartType: EvaChart) => {
  return chartType !== EvaChart.InjectorTotal;
};
export const IS_FORECAST_TRUMPING_DEFAULT_CHART = (chartType: EvaChart) => {
  return (
    chartType === EvaChart.CrossPlot ||
    chartType === EvaChart.BoxPlot ||
    chartType === EvaChart.Probit ||
    chartType === EvaChart.StackedBar ||
    chartType === EvaChart.StackedBarHorizontal ||
    chartType === EvaChart.Mosaic ||
    chartType === EvaChart.TrendDate
  );
};
export const IS_MIDSTREAM_CHART = (chartType: EvaChart) => {
  return (
    chartType === EvaChart.RateDateMidstream ||
    chartType === EvaChart.TotalRateDateMidstream ||
    chartType === EvaChart.CrossPlotMidstream ||
    chartType === EvaChart.BoxPlotMidstream ||
    chartType === EvaChart.StackedBarMidstream ||
    chartType === EvaChart.MosaicMidstream ||
    chartType === EvaChart.TrendDateMidstream ||
    chartType === EvaChart.WellContribution
  );
};
export const IS_FORECAST_COMPARE_CHART = (chartType: EvaChart | ChartTypeLabels) => {
  return (
    chartType === EvaChart.RateCum ||
    chartType === EvaChart.RateTime ||
    chartType === EvaChart.CumTime ||
    chartType === EvaChart.RateDate ||
    chartType === EvaChart.TotalRateCum
  );
};
export type ProductTypeT = {
  name: string;
  items: string[];
};
export const SalesProduct: ProductTypeT[] = [
  {
    name: "Liquids",
    items: [
      ALL_PRODUCT_TYPES.OilCondC5.label,
      ALL_PRODUCT_TYPES.NGL_234.label,
      ALL_PRODUCT_TYPES.NGLMix.label,
      ALL_PRODUCT_TYPES.SalesLiquids.label,
      ALL_PRODUCT_TYPES.C2.label,
      ALL_PRODUCT_TYPES.C3.label,
      ALL_PRODUCT_TYPES.C4.label,
      ALL_PRODUCT_TYPES.C5.label,
      ALL_PRODUCT_TYPES.SalesBOE.label,
      ALL_PRODUCT_TYPES.SalesBOE20.label,
      ALL_PRODUCT_TYPES.SalesBOE30.label
    ]
  },
  {
    name: "Gas",
    items: [
      ALL_PRODUCT_TYPES.SalesGas.label,
      ALL_PRODUCT_TYPES.GasEnergy.label,
      ALL_PRODUCT_TYPES.GasEnergyYieldsSales.label,
      ALL_PRODUCT_TYPES.Shrinkage.label
    ]
  },
  {
    name: "Yields Raw",
    items: [
      ALL_PRODUCT_TYPES.OilCondC5YieldsRaw.label,
      ALL_PRODUCT_TYPES.NGL_234YieldsRaw.label,
      ALL_PRODUCT_TYPES.NGLMixYieldsRaw.label,
      ALL_PRODUCT_TYPES.LiquidsYieldsRaw.label,
      ALL_PRODUCT_TYPES.C2YieldsRaw.label,
      ALL_PRODUCT_TYPES.C3YieldsRaw.label,
      ALL_PRODUCT_TYPES.C4YieldsRaw.label,
      ALL_PRODUCT_TYPES.C5YieldsRaw.label
    ]
  },
  {
    name: "Yields Sales",
    items: [
      ALL_PRODUCT_TYPES.OilCondC5YieldsSales.label,
      ALL_PRODUCT_TYPES.NGL_234YieldsSales.label,
      ALL_PRODUCT_TYPES.NGLMixYieldsSales.label,
      ALL_PRODUCT_TYPES.LiquidsYieldsSales.label,
      ALL_PRODUCT_TYPES.C2YieldsSales.label,
      ALL_PRODUCT_TYPES.C3YieldsSales.label,
      ALL_PRODUCT_TYPES.C4YieldsSales.label,
      ALL_PRODUCT_TYPES.C5YieldsSales.label
    ]
  }
];

export const SalesProductList = SalesProduct.flatMap((category) => category.items);

export const CrossKindProduct = [
  {
    group: ALL_PRODUCT_TYPES.Oil.key,
    items: [ALL_PRODUCT_TYPES.OilCondC5]
  }
];

export const CrossKindProductList = CrossKindProduct.flatMap((category) =>
  category.items.map((item) => item.label)
);

export const ProductKind = {
  Production: 0,
  Sales: 1,
  Throughput: 2,
  Injection: 3,
  Shrinkage: 4
};

export const ChartProduct: ProductTypeT[] = [
  {
    name: "Product",
    items: [
      ALL_PRODUCT_TYPES.Oil.label,
      ALL_PRODUCT_TYPES.Gas.label,
      ALL_PRODUCT_TYPES.Water.label,
      ALL_PRODUCT_TYPES.TotalFluid.label,
      ALL_PRODUCT_TYPES.BOE.label,
      ALL_PRODUCT_TYPES.BOE20.label,
      ALL_PRODUCT_TYPES.BOE30.label,
      ALL_PRODUCT_TYPES.CasingPressure.label,
      ALL_PRODUCT_TYPES.TubingPressure.label
    ]
  },
  {
    name: "Ratio",
    items: [
      ALL_PRODUCT_TYPES.CGR.label,
      ALL_PRODUCT_TYPES.GOR.label,
      ALL_PRODUCT_TYPES.WGR.label,
      ALL_PRODUCT_TYPES.WOR.label,
      ALL_PRODUCT_TYPES.OnePlusWOR.label
    ]
  },
  {
    name: "Percent",
    items: [
      ALL_PRODUCT_TYPES.OilCut.label,
      ALL_PRODUCT_TYPES.WaterCut.label,
      ALL_PRODUCT_TYPES.OnTime.label
    ]
  }
];
export const ChartProductList = ChartProduct.flatMap((category) => category.items);

export const ProductForSelection: ProductTypeT[] = [
  {
    name: "Product",
    items: [
      ALL_PRODUCT_TYPES.Oil.label,
      ALL_PRODUCT_TYPES.Gas.label,
      ALL_PRODUCT_TYPES.Water.label,
      ALL_PRODUCT_TYPES.TotalFluid.label,
      ALL_PRODUCT_TYPES.BOE.label
    ]
  }
];

export const ThroughputUniqueProduct: ProductTypeT[] = [
  {
    name: "Throughput",
    items: [
      ALL_PRODUCT_TYPES.GasInlet.label,
      // ALL_PRODUCT_TYPES.InNetworkInlet.label,
      // ALL_PRODUCT_TYPES.OutOfNetworkInlet.label,
      ALL_PRODUCT_TYPES.InletUtilization.label,
      ALL_PRODUCT_TYPES.GasOutlet.label,
      // ALL_PRODUCT_TYPES.InNetworkOutlet.label,
      ALL_PRODUCT_TYPES.SalesOutlet.label,
      ALL_PRODUCT_TYPES.Fuel.label,
      ALL_PRODUCT_TYPES.Flared.label,
      ALL_PRODUCT_TYPES.Vented.label,
      ALL_PRODUCT_TYPES.PlantShrinkage.label,
      ALL_PRODUCT_TYPES.Sulphur.label,
      ALL_PRODUCT_TYPES.GPWater.label,
      ALL_PRODUCT_TYPES.CO_2.label
    ]
  }
];
export const ThroughputProduct: ProductTypeT[] = [
  {
    name: "Gas",
    items: [
      ALL_PRODUCT_TYPES.GasInlet.label,
      ALL_PRODUCT_TYPES.GasOutlet.label,
      ALL_PRODUCT_TYPES.SalesOutlet.label,
      // ALL_PRODUCT_TYPES.InNetworkInlet.label,
      // ALL_PRODUCT_TYPES.OutOfNetworkInlet.label,
      ALL_PRODUCT_TYPES.InletUtilization.label
      // ALL_PRODUCT_TYPES.InNetworkOutlet.label,
    ]
  },
  {
    name: "Products",
    items: [
      ALL_PRODUCT_TYPES.NGL_234.label,
      ALL_PRODUCT_TYPES.NGLMix.label,
      ALL_PRODUCT_TYPES.C2.label,
      ALL_PRODUCT_TYPES.C3.label,
      ALL_PRODUCT_TYPES.C4.label,
      ALL_PRODUCT_TYPES.C5.label,
      ALL_PRODUCT_TYPES.Sulphur.label,
      ALL_PRODUCT_TYPES.GPWater.label,
      ALL_PRODUCT_TYPES.CO_2.label
    ]
  },
  {
    name: "Yields Inlet",
    items: [
      ALL_PRODUCT_TYPES.NGL_234YieldsInlet.label,
      ALL_PRODUCT_TYPES.NGLMixYieldsInlet.label,
      ALL_PRODUCT_TYPES.C2YieldsInlet.label,
      ALL_PRODUCT_TYPES.C3YieldsInlet.label,
      ALL_PRODUCT_TYPES.C4YieldsInlet.label,
      ALL_PRODUCT_TYPES.C5YieldsInlet.label,
      ALL_PRODUCT_TYPES.SulphurYieldsInlet.label,
      ALL_PRODUCT_TYPES.GPWaterYieldsInlet.label,
      ALL_PRODUCT_TYPES.CO_2YieldsInlet.label
    ]
  },
  {
    name: "Yields Outlet",
    items: [
      ALL_PRODUCT_TYPES.NGL_234YieldsOutlet.label,
      ALL_PRODUCT_TYPES.NGLMixYieldsOutlet.label,
      ALL_PRODUCT_TYPES.C2YieldsOutlet.label,
      ALL_PRODUCT_TYPES.C3YieldsOutlet.label,
      ALL_PRODUCT_TYPES.C4YieldsOutlet.label,
      ALL_PRODUCT_TYPES.C5YieldsOutlet.label,
      ALL_PRODUCT_TYPES.SulphurYieldsOutlet.label,
      ALL_PRODUCT_TYPES.GPWaterYieldsOutlet.label,
      ALL_PRODUCT_TYPES.CO_2YieldsOutlet.label
    ]
  }
];

export const ThroughputProductList = ThroughputProduct.flatMap(
  (category) => category.items
);

export const InjectionProduct: ProductTypeT[] = [
  {
    name: "Product",
    items: [
      ALL_PRODUCT_TYPES.WaterInj.label,
      ALL_PRODUCT_TYPES.GasInj.label,
      ALL_PRODUCT_TYPES.PolymerInj.label,
      ALL_PRODUCT_TYPES.TotalLiquidInj.label,
      ALL_PRODUCT_TYPES.CO_2Inj.label,
      ALL_PRODUCT_TYPES.AcidGasInj.label,
      ALL_PRODUCT_TYPES.SolventInj.label,
      ALL_PRODUCT_TYPES.TotalGasInj.label,
      ALL_PRODUCT_TYPES.SteamInj.label
    ]
  },
  {
    name: "Ratio",
    items: [
      ALL_PRODUCT_TYPES.ISOR.label,
      ALL_PRODUCT_TYPES.CSOR.label,
      ALL_PRODUCT_TYPES.IWB.label,
      ALL_PRODUCT_TYPES.CWB.label,
      ALL_PRODUCT_TYPES.IVRR.label,
      ALL_PRODUCT_TYPES.VRR.label
    ]
  }
];

export const InjectionProductList = InjectionProduct.flatMap(
  (category) => category.items
);

export const ProductionTime: ProductTypeT[] = [
  {
    name: "Time",
    items: [ALL_PRODUCT_TYPES.Hours.label]
  }
];

export const ShrinkageProduct: ProductTypeT[] = [
  {
    name: "Shrinkage Gas",
    items: [
      ALL_PRODUCT_TYPES.Fuel.label,
      ALL_PRODUCT_TYPES.Flared.label,
      ALL_PRODUCT_TYPES.Vented.label,
      ALL_PRODUCT_TYPES.FFV.label,
      ALL_PRODUCT_TYPES.LiquidsShrinkage.label,
      ALL_PRODUCT_TYPES.AcidGasShrinkage.label,
      ALL_PRODUCT_TYPES.AcidGasDisposition.label
    ]
  },
  {
    name: "Percent Inlet",
    items: [
      ALL_PRODUCT_TYPES.FuelInletRate.label,
      ALL_PRODUCT_TYPES.FlaredInletRate.label,
      ALL_PRODUCT_TYPES.VentedInletRate.label,
      ALL_PRODUCT_TYPES.FFVInletRate.label,
      ALL_PRODUCT_TYPES.LiquidsShrinkageInletRate.label,
      ALL_PRODUCT_TYPES.AcidGasShrinkageInletRate.label,
      ALL_PRODUCT_TYPES.AcidGasDispositionInletRate.label,
      ALL_PRODUCT_TYPES.TotalShrinkageInlet.label
    ]
  },
  {
    name: "Percent Outlet",
    items: [
      ALL_PRODUCT_TYPES.FuelOutletRate.label,
      ALL_PRODUCT_TYPES.FlaredOutletRate.label,
      ALL_PRODUCT_TYPES.VentedOutletRate.label,
      ALL_PRODUCT_TYPES.FFVOutletRate.label,
      ALL_PRODUCT_TYPES.LiquidsShrinkageOutletRate.label,
      ALL_PRODUCT_TYPES.AcidGasShrinkageOutletRate.label,
      ALL_PRODUCT_TYPES.AcidGasDispositionOutletRate.label,
      ALL_PRODUCT_TYPES.TotalShrinkageOutlet.label
    ]
  }
];

export const ShrinkageProductList = ShrinkageProduct.flatMap(
  (category) => category.items
);

export const MaterialBalanceTimeChartProducts = [
  ALL_PRODUCT_TYPES.Oil.label,
  ALL_PRODUCT_TYPES.Gas.label,
  ALL_PRODUCT_TYPES.Water.label,
  ALL_PRODUCT_TYPES.TotalFluid.label,
  ALL_PRODUCT_TYPES.BOE.label,
  ALL_PRODUCT_TYPES.BOE20.label,
  ALL_PRODUCT_TYPES.BOE30.label
];

export const RecoveryTimeChartProducts = [
  ALL_PRODUCT_TYPES.Oil.label,
  ALL_PRODUCT_TYPES.Gas.label
];

export const PERFORMANCE_PRODUCT_LIST = [
  ALL_PRODUCT_TYPES.Oil,
  ALL_PRODUCT_TYPES.Gas,
  ALL_PRODUCT_TYPES.Water,
  ALL_PRODUCT_TYPES.TotalFluid,
  ALL_PRODUCT_TYPES.BOE,
  // Order here is functional: getColumnPropertyTree sorts the Performance group by
  // index, so each ratio has to sit next to its 6:1 sibling.
  ALL_PRODUCT_TYPES.BOE20,
  ALL_PRODUCT_TYPES.BOE30,
  ALL_PRODUCT_TYPES.CGR,
  ALL_PRODUCT_TYPES.GOR,
  ALL_PRODUCT_TYPES.WGR,
  ALL_PRODUCT_TYPES.WOR,
  ALL_PRODUCT_TYPES.ISOR,
  ALL_PRODUCT_TYPES.CSOR,
  ALL_PRODUCT_TYPES.IWB,
  ALL_PRODUCT_TYPES.CWB,
  ALL_PRODUCT_TYPES.OilCut,
  ALL_PRODUCT_TYPES.WaterCut,
  ALL_PRODUCT_TYPES.Hours,
  ALL_PRODUCT_TYPES.OnTime,
  ALL_PRODUCT_TYPES.SteamInj,
  ALL_PRODUCT_TYPES.GasInj,
  ALL_PRODUCT_TYPES.WaterInj,
  ALL_PRODUCT_TYPES.PolymerInj,
  ALL_PRODUCT_TYPES.CO_2Inj,
  ALL_PRODUCT_TYPES.AcidGasInj,
  ALL_PRODUCT_TYPES.SolventInj,
  ALL_PRODUCT_TYPES.TotalLiquidInj,
  ALL_PRODUCT_TYPES.TotalGasInj,
  ALL_PRODUCT_TYPES.SalesGas,
  ALL_PRODUCT_TYPES.GasEnergy,
  ALL_PRODUCT_TYPES.GasEnergyYieldsSales,
  ALL_PRODUCT_TYPES.Shrinkage,
  ALL_PRODUCT_TYPES.TotalShrinkageInlet,
  ALL_PRODUCT_TYPES.TotalShrinkageOutlet,
  ALL_PRODUCT_TYPES.C2,
  ALL_PRODUCT_TYPES.C3,
  ALL_PRODUCT_TYPES.C4,
  ALL_PRODUCT_TYPES.C5,
  ALL_PRODUCT_TYPES.SalesLiquids,
  ALL_PRODUCT_TYPES.SalesBOE,
  ALL_PRODUCT_TYPES.SalesBOE20,
  ALL_PRODUCT_TYPES.SalesBOE30,
  ALL_PRODUCT_TYPES.OilCondC5,
  ALL_PRODUCT_TYPES.NGLMix,
  ALL_PRODUCT_TYPES.NGL_234,
  ALL_PRODUCT_TYPES.C2YieldsRaw,
  ALL_PRODUCT_TYPES.C3YieldsRaw,
  ALL_PRODUCT_TYPES.C4YieldsRaw,
  ALL_PRODUCT_TYPES.C5YieldsRaw,
  ALL_PRODUCT_TYPES.OilCondC5YieldsRaw,
  ALL_PRODUCT_TYPES.LiquidsYieldsRaw,
  ALL_PRODUCT_TYPES.NGLMixYieldsRaw,
  ALL_PRODUCT_TYPES.NGL_234YieldsRaw,
  ALL_PRODUCT_TYPES.C2YieldsSales,
  ALL_PRODUCT_TYPES.C3YieldsSales,
  ALL_PRODUCT_TYPES.C4YieldsSales,
  ALL_PRODUCT_TYPES.C5YieldsSales,
  ALL_PRODUCT_TYPES.LiquidsYieldsSales,
  ALL_PRODUCT_TYPES.OilCondC5YieldsSales,
  ALL_PRODUCT_TYPES.NGLMixYieldsSales,
  ALL_PRODUCT_TYPES.NGL_234YieldsSales,
  ALL_PRODUCT_TYPES.Fuel,
  ALL_PRODUCT_TYPES.FuelInletRate,
  ALL_PRODUCT_TYPES.FuelOutletRate,
  ALL_PRODUCT_TYPES.Flared,
  ALL_PRODUCT_TYPES.FlaredInletRate,
  ALL_PRODUCT_TYPES.FlaredOutletRate,
  ALL_PRODUCT_TYPES.Vented,
  ALL_PRODUCT_TYPES.VentedInletRate,
  ALL_PRODUCT_TYPES.VentedOutletRate,
  ALL_PRODUCT_TYPES.PlantShrinkage,
  ALL_PRODUCT_TYPES.Sulphur,
  ALL_PRODUCT_TYPES.SulphurYieldsInlet,
  ALL_PRODUCT_TYPES.SulphurYieldsOutlet,
  ALL_PRODUCT_TYPES.GPWater,
  ALL_PRODUCT_TYPES.GPWaterYieldsInlet,
  ALL_PRODUCT_TYPES.GPWaterYieldsOutlet,
  ALL_PRODUCT_TYPES.CO_2,
  ALL_PRODUCT_TYPES.CO_2YieldsInlet,
  ALL_PRODUCT_TYPES.CO_2YieldsOutlet,
  ALL_PRODUCT_TYPES.InletUtilization,
  ALL_PRODUCT_TYPES.LiquidsShrinkage,
  ALL_PRODUCT_TYPES.LiquidsShrinkageInletRate,
  ALL_PRODUCT_TYPES.LiquidsShrinkageOutletRate,
  ALL_PRODUCT_TYPES.AcidGasShrinkage,
  ALL_PRODUCT_TYPES.AcidGasShrinkageInletRate,
  ALL_PRODUCT_TYPES.AcidGasShrinkageOutletRate,
  ALL_PRODUCT_TYPES.AcidGasDisposition,
  ALL_PRODUCT_TYPES.AcidGasDispositionInletRate,
  ALL_PRODUCT_TYPES.AcidGasDispositionOutletRate,
  ALL_PRODUCT_TYPES.FFV,
  ALL_PRODUCT_TYPES.FFVInletRate,
  ALL_PRODUCT_TYPES.FFVOutletRate,
  ALL_PRODUCT_TYPES.GasInlet,
  // ALL_PRODUCT_TYPES.InNetworkInlet,
  // ALL_PRODUCT_TYPES.OutOfNetworkInlet,
  ALL_PRODUCT_TYPES.GasOutlet,
  // ALL_PRODUCT_TYPES.InNetworkOutlet,
  ALL_PRODUCT_TYPES.SalesOutlet
];

export const FORECAST_PRODUCT_LIST = [
  ALL_PRODUCT_TYPES.Oil,
  ALL_PRODUCT_TYPES.Gas,
  ALL_PRODUCT_TYPES.Water
];

export const PRODUCT_COLOR = {
  condensate: "#1bce0c",
  gas: "#fd0e1a",
  oil: "#186b2d",
  water: "#025aff"
};

export const PRODUCT_LIST = [
  ALL_PRODUCT_TYPES.Oil.label,
  ALL_PRODUCT_TYPES.Gas.label,
  ALL_PRODUCT_TYPES.Water.label,
  ALL_PRODUCT_TYPES.TotalFluid.label,
  ALL_PRODUCT_TYPES.GOR.label,
  ALL_PRODUCT_TYPES.CGR.label,
  ALL_PRODUCT_TYPES.WGR.label,
  ALL_PRODUCT_TYPES.OilCut.label,
  ALL_PRODUCT_TYPES.WaterCut.label
];

// ** Start Multiproduct Constants ** //

export const ChartTypeMultiProduct = [
  {
    name: "Chart Type",
    items: [
      ALL_CHART_TYPES.RateCum.label,
      ALL_CHART_TYPES.RateTime.label,
      ALL_CHART_TYPES.CumTime.label,
      ALL_CHART_TYPES.RateDate.label,
      ALL_CHART_TYPES.TotalRateCum.label
    ]
  }
];

export const ProductsForMultiProductChart = [
  {
    name: "Product",
    items: [
      ALL_PRODUCT_TYPES.Oil.label,
      ALL_PRODUCT_TYPES.Gas.label,
      ALL_PRODUCT_TYPES.Water.label,
      ALL_PRODUCT_TYPES.TotalFluid.label,
      ALL_PRODUCT_TYPES.BOE.label,
      ALL_PRODUCT_TYPES.WaterInj.label,
      ALL_PRODUCT_TYPES.GasInj.label,
      ALL_PRODUCT_TYPES.SteamInj.label,
      ALL_PRODUCT_TYPES.PolymerInj.label,
      ALL_PRODUCT_TYPES.CO_2Inj.label,
      ALL_PRODUCT_TYPES.AcidGasInj.label,
      ALL_PRODUCT_TYPES.SolventInj.label,
      ALL_PRODUCT_TYPES.TotalLiquidInj.label,
      ALL_PRODUCT_TYPES.TotalGasInj.label
    ]
  },
  {
    name: "Ratio",
    items: [
      ALL_PRODUCT_TYPES.CGR.label,
      ALL_PRODUCT_TYPES.GOR.label,
      ALL_PRODUCT_TYPES.WGR.label,
      ALL_PRODUCT_TYPES.WOR.label,
      ALL_PRODUCT_TYPES.OnePlusWOR.label,
      ALL_PRODUCT_TYPES.ISOR.label,
      ALL_PRODUCT_TYPES.CSOR.label,
      ALL_PRODUCT_TYPES.IWB.label,
      ALL_PRODUCT_TYPES.CWB.label
    ]
  },
  {
    name: "Percent",
    items: [ALL_PRODUCT_TYPES.OilCut.label, ALL_PRODUCT_TYPES.WaterCut.label]
  }
];

// ** End Multiproduct Constants ** //

export const CHART_ACTION = Object.freeze({
  brushStart: {
    type: "takeGlobalCursor",
    key: "brush",
    brushOption: { brushType: "polygon" }
  },
  brushClear: {
    type: "brush",
    command: "clear",
    areas: []
  },
  brushEnd: {
    type: "takeGlobalCursor",
    key: "brush",
    brushOption: {
      brushType: false,
      brushMode: "single"
    }
  }
});
export const ORIGIN = { x: 0, y: 0 };
// chart component events
export const CHART_EVENT = Object.freeze({
  snapshotDownloadClick: "onSnapshotDownloadClicked",
  fullscreenToggle: "onFullscreenToggle"
});

// x and y-axis range defaults for fluids
export const CHART_RANGE = Object.freeze({
  oil: { xMin: 0, xMax: "", yMin: 0, yMax: "" },
  water: { xMin: 0, xMax: "", yMin: 0, yMax: "" },
  gas: { xMin: 0, xMax: "", yMin: 0, yMax: "" },
  cond: { xMin: 0, xMax: "", yMin: 0, yMax: "" },
  gor: { xMin: 0, xMax: "", yMin: 0, yMax: 10000 },
  wor: { xMin: 0, xMax: "", yMin: 0, yMax: 99 },
  ogr: { xMin: 0, xMax: "", yMin: 0, yMax: 500 },
  cgr: { xMin: 0, xMax: "", yMin: 0, yMax: 500 },
  wgr: { xMin: 0, xMax: "", yMin: 0, yMax: 250 },
  totalFluid: { xMin: 0, xMax: "", yMin: 0, yMax: "" },
  waterCut: { xMin: 0, xMax: "", yMin: 0, yMax: "" },
  oilCut: { xMin: 0, xMax: "", yMin: 0, yMax: "" }
});

// Object of plotly scale values
export const PLOTLY_SCALE = Object.freeze({
  default: "-",
  log: "log",
  linear: "linear",
  date: "date",
  category: "category",
  multi: "multicategory"
});

// Plotly default config object
export const PLOTLY_DEFAULT_CONFIG = {
  /*
    doubleClick: false, 'reset', 'autosize' or 'reset+autosize'
    Disable zoom out on double clicking axis
  */
  displaylogo: false,
  locale: "en",
  modeBarButtonsToRemove: ["autoScale2d", "lasso2d", "toImage", "toggleSpikelines"],
  responsive: true
};

export const SNAPSHOT_SIZE = Object.freeze({
  hr1: {
    label: "HR1",
    width: 1152,
    height: 681
  },
  hr2: {
    label: "HR2",
    width: 2416,
    height: 956
  },
  s1: {
    label: "S1",
    width: 1184,
    height: 938
  },
  vr1: {
    label: "VR1",
    width: 774,
    height: 938
  },
  custom: {
    label: "Custom",
    width: "",
    height: ""
  }
});

export const Vintage = {
  id: "",
  property: "Dates.Vintage_Year",
  title: "Vintage",
  dataType: "Integer",
  canBin: true
};
export const HzLength = {
  id: "",
  property: "Well_Design.HzLength_m",
  title: FIELD_NAMES.HZ_LENGTH,
  dataType: "Number",
  canBin: true
};

export type ChartProductGroupT =
  | "Production"
  | "Sales"
  | "Throughput"
  | "Shrinkage"
  | "Injection";

type Point = { x: number; y: number };
export type ChartCapabilitiesT =
  | "DataSourceSelection"
  | "Forecast"
  | "CD_PD"
  | "TimeStep"
  | "TypeWell"
  | "Normalize";

export type ScreenshotT = {
  visible: boolean;
  preset: IChartScreenshotSettings | null;
};

export type StateT = {
  // TODO: temporary fix. lift state and extract all absolute positioned components to root
  axisInputVisible: boolean;
  bounds: DOMRect | null;
  brushEnd: boolean;
  brushEndPosition: Point | null;
  brushedList: Array<string> | null;
  busy: boolean;
  contextMenuPosition: Point | null;
  id: string;
  instance: EChartsType;
  isFullscreen: boolean;
  isJitterPlotXBinDefaulting?: boolean;
  isProbitDataFromLineOfBestFit?: boolean;
  lasso: boolean;
  overflowIndex: number;
  options;
  response: IChartResult;
  request;
  screenshot: ScreenshotT;
  settings: IChartSettings | null;
  chartCapabilities: { name: ChartCapabilitiesT; key: string }[];
  availableChartProductGroups: ChartProductGroupT[];
  availableChartTypes: ChartTypeT[];
  entityKind: EntityKind;
  unitSlopePosition: lineCoordinates;
  halfSlopePosition: lineCoordinates;
  quarterSlopePosition: lineCoordinates;
};

const defaultChartState = {
  axisInputVisible: false,
  bounds: null,
  brushEnd: false,
  brushEndPosition: null,
  brushedList: null,
  busy: false,
  contextMenuPosition: null,
  id: "",
  instance: null,
  isFullscreen: false,
  isJitterPlotXBinDefaulting: false,
  isProbitDataFromLineOfBestFit: false,
  lasso: false,
  overflowIndex: 0,
  options: null,
  response: null,
  request: null,
  screenshot: {
    visible: false,
    preset: null
  },
  settings: null,
  chartCapabilities: [],
  availableChartProductGroups: [],
  availableChartTypes: [],
  entityKind: EntityKind.Well,
  unitSlopePosition: null,
  halfSlopePosition: null,
  quarterSlopePosition: null
} as StateT;

export const DEFAULT_CHARTS: IChartSettings[] = [
  {
    showP10: false,
    showP50: false,
    showP90: false,
    id: "Chart 0",
    screenshot: false,
    legendPosition: "NE",
    log: {
      x: false,
      y: false
    },
    showOnlySelectedWells: false,
    chartType: "Rate Cum",
    chartTypeId: 1,
    product: "Oil + Cond",
    groupByListVisible: false,
    groupBy: null,
    showToolbar: true,
    showNormalize: true,
    enableZoom: true,
    forecast: true,
    average: false,
    typewells: true,
    producing: false,
    normalizeBy: {
      field: "Well_Design.HzLength_m",
      display: FIELD_NAMES.HZ_LENGTH,
      per: 100,
      unit: "",
      useMultilinearNormalization: false,
      threshold: 0,
      lowerScalar: 1,
      higherScalar: 1
    },
    useNormalizeBy: false,
    showActiveWellOnly: true,
    timeStep: TimeStep.Month,
    legend: { visible: false, position: null },
    palette: false,
    snapshot: false,
    fullscreen: false,
    sum: false,
    source: EvaSource.Public,
    cutoff: 50,
    shutInMonths: 3,
    survivorBias: true,
    useWeightedRatioAverage: true,
    rangeVisible: false,
    xAxisParamColor: "#f6768e",
    yAxisParamColor: "#f6768e",
    xAxisParamVisible: false,
    yAxisParamVisible: false,
    xAxisParamListVisible: false,
    yAxisParamListVisible: false,
    busy: false,
    range: { x: [], y: [] },
    chartTypeParam: {
      x: Vintage,
      y: HzLength
    },
    stackedBarCalculation: CalculationMethod.Count,
    pressureChartSettings: {
      product: ALL_PRODUCT_TYPES.Oil.key,
      productivityIndex: false
    },
    axisMinMax: null,
    dotOpacity: DEFAULT_DOT_OPACITY,
    dotSize: DEFAULT_POINT_SIZE,
    showUnitSlope: false,
    showHalfSlope: false,
    showQuarterSlope: false,
    movingAverageDays: 5,
    showAnnotations: false
  },
  {
    id: "Chart 1",
    showP10: false,
    showP50: false,
    showP90: false,
    showUnitSlope: false,
    showHalfSlope: false,
    showQuarterSlope: false,
    screenshot: false,
    movingAverageDays: 5,
    legendPosition: "NE",
    log: {
      x: false,
      y: false
    },
    showOnlySelectedWells: false,
    chartType: "Mosaic",
    chartTypeId: 1,
    product: "Gas",
    groupByListVisible: false,
    groupBy: null,
    forecast: true,
    showToolbar: true,
    showNormalize: true,
    enableZoom: true,
    shutInMonths: 3,
    normalizeBy: {
      field: "Well_Design.HzLength_m",
      display: FIELD_NAMES.HZ_LENGTH,
      per: 100,
      unit: "",
      useMultilinearNormalization: false,
      threshold: 0,
      lowerScalar: 1,
      higherScalar: 1
    },
    useNormalizeBy: false,
    average: false,
    typewells: true,
    producing: false,
    showActiveWellOnly: true,
    sum: false,
    source: EvaSource.Public,
    cutoff: 50,
    survivorBias: true,
    useWeightedRatioAverage: true,
    timeStep: TimeStep.Month,
    legend: { visible: false, position: null },
    palette: false,
    snapshot: false,
    fullscreen: false,
    rangeVisible: false,
    xAxisParamColor: "#f6768e",
    yAxisParamColor: "#f6768e",
    xAxisParamVisible: false,
    yAxisParamVisible: false,
    xAxisParamListVisible: false,
    yAxisParamListVisible: false,
    stackedBarCalculation: CalculationMethod.Count,
    pressureChartSettings: {
      product: ALL_PRODUCT_TYPES.Oil.key,
      productivityIndex: false
    },
    busy: false,
    range: { x: [], y: [] },
    chartTypeParam: {
      x: Vintage,
      y: HzLength
    },
    axisMinMax: null,
    dotOpacity: DEFAULT_DOT_OPACITY,
    dotSize: DEFAULT_POINT_SIZE,
    showAnnotations: false
  },
  {
    id: "Chart 2",
    showP10: false,
    showP50: false,
    showP90: false,
    showUnitSlope: false,
    showHalfSlope: false,
    showQuarterSlope: false,
    movingAverageDays: 5,
    screenshot: false,
    legendPosition: "NE",
    log: {
      x: false,
      y: false
    },
    showOnlySelectedWells: false,
    chartType: "Rate Cum",
    chartTypeId: 1,
    product: "Water",
    shutInMonths: 3,
    groupByListVisible: false,
    groupBy: null,
    forecast: true,
    showToolbar: true,
    showNormalize: true,
    enableZoom: true,
    normalizeBy: {
      field: "Well_Design.HzLength_m",
      display: FIELD_NAMES.HZ_LENGTH,
      per: 100,
      unit: "",
      useMultilinearNormalization: false,
      threshold: 0,
      lowerScalar: 1,
      higherScalar: 1
    },
    useNormalizeBy: false,
    average: false,
    typewells: true,
    producing: false,
    showActiveWellOnly: true,
    sum: false,
    source: EvaSource.Public,
    cutoff: 50,
    survivorBias: true,
    useWeightedRatioAverage: true,
    timeStep: TimeStep.Month,
    legend: { visible: false, position: null },
    palette: false,
    snapshot: false,
    fullscreen: false,
    rangeVisible: false,
    xAxisParamColor: "#f6768e",
    yAxisParamColor: "#f6768e",
    xAxisParamVisible: false,
    yAxisParamVisible: false,
    xAxisParamListVisible: false,
    yAxisParamListVisible: false,
    busy: false,
    range: { x: [], y: [] },
    stackedBarCalculation: CalculationMethod.Count,
    pressureChartSettings: {
      product: ALL_PRODUCT_TYPES.Oil.key,
      productivityIndex: false
    },
    chartTypeParam: {
      x: Vintage,
      y: HzLength
    },
    axisMinMax: null,
    dotOpacity: DEFAULT_DOT_OPACITY,
    dotSize: DEFAULT_POINT_SIZE,
    showAnnotations: false
  },
  {
    id: "Chart 3",
    showP10: false,
    showP50: false,
    showP90: false,
    screenshot: false,
    legendPosition: "NE",
    log: {
      x: false,
      y: false
    },
    showOnlySelectedWells: false,
    chartType: "Total Rate Date",
    chartTypeId: 1,
    product: "Gas",
    groupByListVisible: false,
    groupBy: null,
    forecast: true,
    showToolbar: true,
    showNormalize: true,
    enableZoom: true,
    shutInMonths: 3,
    normalizeBy: {
      field: "Well_Design.HzLength_m",
      display: FIELD_NAMES.HZ_LENGTH,
      per: 100,
      unit: "",
      useMultilinearNormalization: false,
      threshold: 0,
      lowerScalar: 1,
      higherScalar: 1
    },
    useNormalizeBy: false,
    average: false,
    typewells: true,
    producing: false,
    showActiveWellOnly: true,
    sum: false,
    source: EvaSource.Public,
    cutoff: 50,
    survivorBias: true,
    useWeightedRatioAverage: true,
    timeStep: TimeStep.Month,
    legend: { visible: false, position: null },
    palette: false,
    snapshot: false,
    fullscreen: false,
    rangeVisible: false,
    xAxisParamColor: "#f6768e",
    yAxisParamColor: "#f6768e",
    xAxisParamVisible: false,
    yAxisParamVisible: false,
    xAxisParamListVisible: false,
    yAxisParamListVisible: false,
    busy: false,
    range: { x: [], y: [] },
    movingAverageDays: 5,
    stackedBarCalculation: CalculationMethod.Count,
    pressureChartSettings: {
      product: ALL_PRODUCT_TYPES.Oil.key,
      productivityIndex: false
    },
    chartTypeParam: {
      x: Vintage,
      y: HzLength
    },
    axisMinMax: null,
    dotOpacity: DEFAULT_DOT_OPACITY,
    dotSize: DEFAULT_POINT_SIZE,
    showUnitSlope: false,
    showHalfSlope: false,
    showQuarterSlope: false,
    showAnnotations: false
  }
];

export const CHART_SIZE_ERROR = "Error - Series Limit Exceeded";
export const CHART_WELL_ERROR = "Error - Well Count Exceeded";
export const CHART_EMPTY_ERROR = "noData";

export const PARAM_FIELD = {
  OilCumToDate: {
    id: "",
    property: "Performance.Oil.CumToDate",
    title: "Cum To Date (Mbbl)",
    dataType: "Number",
    canBin: true,
    product: "Oil",
    defaultUnit: "Mbbl"
  },
  Oil12MCalTime: {
    id: "",
    property: "Performance.Oil.TwelveMCalTimeCum",
    title: "12M CalTime Cum (Mbbl)",
    dataType: "Number",
    canBin: true,
    product: "Oil",
    defaultUnit: "Mbbl"
  },
  Oil12MCalTimeWithForecast: {
    id: "",
    property: "Performance.Oil.TwelveMCalTimeCum.fcst",
    title: "12M CalTime Cum + Forecast (Mbbl)",
    dataType: "Number",
    canBin: true,
    product: "Oil",
    defaultUnit: "Mbbl"
  },
  Vintage_YearMonth: {
    id: "",
    property: "Dates.Vintage_YearMonth",
    title: "Vintage (Year-Month)",
    dataType: "Text",
    canBin: false
  },
  Vintage_Year: {
    id: "",
    property: "Dates.Vintage_Year",
    title: "Vintage (Year)",
    dataType: "Integer",
    canBin: true
  },
  HzLength: {
    id: "",
    property: "Well_Design.HzLength_m",
    title: FIELD_NAMES.HZ_LENGTH,
    dataType: "Number",
    canBin: true
  },
  ResourcePlay: {
    id: "",
    property: "Header.ResourcePlay",
    title: "Resource Play",
    dataType: "Text",
    canBin: false
  },
  FacilityStartDateYear: {
    id: "",
    property: "Header.FacilityStartDate_YearMonth",
    title: "Start Date (Year-Month)",
    dataType: "Text",
    canBin: false
  },
  Gas12MCalTime: {
    id: "",
    property: "Performance.Gas.TwelveMCalTimeCum",
    title: "12M CalTime Cum (MMcf)",
    dataType: "Number",
    canBin: true,
    product: "Gas",
    defaultUnit: "MMcf"
  },
  CurrentCalRate: {
    id: "",
    property: "Performance.Gas.CurrentCalRate",
    title: "Current Cal. Rate (Mcf/d)",
    dataType: "Number",
    canBin: true,
    product: "Gas",
    defaultUnit: "Mcf"
  }
};

export const DEFAULT_CHART_STATE: StateT = {
  ...defaultChartState,
  settings: DEFAULT_CHARTS[0]
};

// Plotly constants
const grid_color = "#000000";
const modebar_default_icon_color = "#abbace";
const modebar_active_icon_color = "#40546b";
const font_color = "#314155";
const transparent_hex = "#00000000";

// Plotly default layout object
export const PLOTLY_DEFAULT_LAYOUT = {
  /* TODO: set colorway */
  autosize: true,
  title: {
    text: "Plot Title"
  },
  font: {
    color: font_color,
    size: 12
  },
  margin: {
    l: 46,
    r: 16,
    b: 54,
    t: 25
  },
  showlegend: false,
  legend: {
    font: {
      family: "apercu"
    }
  },
  modebar: {
    activecolor: modebar_active_icon_color,
    color: modebar_default_icon_color,
    bgcolor: transparent_hex,
    orientation: "v"
  },
  /* Hovermode: "x" | "y" | "closest" | false */
  hovermode: "closest",
  /* Dragmode:  "zoom" | "pan" | "select" | "lasso" | "orbit" | "turntable" | false */
  hoverlabel: {
    font: {
      family: "apercu",
      size: 14
    }
  },
  dragmode: "zoom",
  /* No color for paper and plot */
  plot_bgcolor: transparent_hex,
  paper_bgcolor: transparent_hex,
  xaxis: {
    automargin: true,
    color: grid_color,
    gridcolor: grid_color,
    tickformat: "yyyy-dd",
    showdividers: false,
    showline: false,
    zeroline: false
  },
  yaxis: {
    automargin: true,
    color: grid_color,
    gridcolor: grid_color,
    showdividers: false,
    showline: false,
    zeroline: false
  },
  datarevision: 1
};

export const ForecastRatios = [
  { label: "GOR", value: 5 },
  { label: "CGR", value: 6 },
  { label: "WGR", value: 7 },
  { label: "OGR", value: 8 },
  { label: "WOR", value: 9 }
];

export const ForecastProducts = [
  { label: "Oil", value: 1 },
  { label: "Gas", value: 2 },
  { label: "Water", value: 3 },
  ...ForecastRatios
];

export const EMPTY_CHART_RESULT = {
  title: "",
  messages: [
    {
      message: "",
      messageType: CHART_EMPTY_ERROR,
      entities: []
    }
  ],
  errorMessage: "",
  messageType: CHART_EMPTY_ERROR,
  errorEntities: [],
  series: [],
  layout: {
    xAxis: {
      title: "",
      defaultAxisType: "linear",
      labels: [],
      min: 0,
      max: 0,
      interval: 0,
      data: [],
      logMin: null,
      yScaleToX: 1,
      axisIsDate: false,
      isSecondaryYAxis: false
    },
    yAxis: {
      title: "",
      defaultAxisType: "linear",
      labels: [],
      min: 0,
      max: 0,
      interval: 0,
      data: [],
      logMin: null,
      yScaleToX: 1,
      axisIsDate: false,
      isSecondaryYAxis: false
    },
    yAxis2: [],
    xAxis2: []
  },
  requestId: "",
  uwis: [],
  uwiCoordinates: {},
  segments: [],
  constants: [],
  wellSources: {
    productionDataSources: [],
    forecastSources: []
  },
  productionSources: "",
  syncStatus: {
    isForecastSynced: false,
    isProductionSynced: false,
    syncedSchema: "",
    warningMessage: ""
  },
  normalizeData: null
} as IChartResult;

export const ENTITY_NAME_PROPERTY = "Header.EntityName";

export const WellOperatorGroupBy: IGroupBy = {
  title: "Operator",
  property: "Header.Operator_Short",
  groupByField: "Header.Operator_Short",
  pdenSource: "Public",
  canBin: false,
  dataType: "Text",
  categoryId: 1,
  tooltip: "",
  display: "",
  entityKind: EntityKind.Facility,
  bin: {
    BinSize: "",
    MinSize: "",
    GreaterThan: "",
    BinType: "BinSize",
    LessThan: ""
  }
};

export const DEFAULT_CHART_TYPE_PARAM_X_PROPERTIES = {
  group: "Dates",
  subgroup: "Date Groups",
  product: "",
  property: "Dates.Vintage_YearMonth",
  title: "Vintage (Year-Month)",
  defaultUnit: "Year-Month",
  dataType: "Text",
  canBin: false,
  bin: null,
  performanceDateRange: undefined,
  // Declared so createMergeWithDefaults keeps it — getAllowedKeys builds its
  // allow-list from this object, and an undeclared key is dropped from a restored axis.
  forecastFolderExplicit: undefined,
  // Declared for the same reason: the forecast toggle parks the axis qualification here while
  // forecast is off, and an undeclared key does not survive a restore or a chart-type switch.
  forecastPropertySuffix: undefined
} as Partial<IGroupBy>;

export const DEFAULT_CHART_TYPE_PARAM_Y_PROPERTIES = {
  group: "Performance",
  subgroup: "Calendar Time",
  product: "Oil",
  property: "Performance.Oil.TwelveMCalTimeCum.fcst",
  title: "12M CalTime Cum + Forecast (Mbbl)",
  defaultUnit: "Mbbl",
  dataType: "Number",
  tooltip: "",
  canBin: true,
  forecastFolder: EVA_RESEARCH_NAME,
  isForecastToggleOn: true,
  bin: null,
  performanceDateRange: undefined,
  // Declared so createMergeWithDefaults keeps it — getAllowedKeys builds its
  forecastFolderExplicit: undefined,
  // Declared for the same reason: the forecast toggle parks the axis qualification here while
  // forecast is off, and an undeclared key does not survive a restore or a chart-type switch.
  forecastPropertySuffix: undefined
} as Partial<IGroupBy>;

export const DEFAULT_PROBIT_CHART_TYPE_PARAM_X_PROPERTIES = {
  group: "Performance",
  subgroup: "Calendar Time",
  product: "Oil",
  property: "Performance.Oil.TwelveMCalTimeCum.fcst",
  title: "12M CalTime Cum + Forecast (Mbbl)",
  defaultUnit: "Mbbl",
  dataType: "Number",
  tooltip: "",
  canBin: true,
  forecastFolder: EVA_RESEARCH_NAME,
  isForecastToggleOn: true,
  performanceDateRange: undefined,
  // Declared so createMergeWithDefaults keeps it — getAllowedKeys builds its
  forecastFolderExplicit: undefined,
  // Declared for the same reason: the forecast toggle parks the axis qualification here while
  // forecast is off, and an undeclared key does not survive a restore or a chart-type switch.
  forecastPropertySuffix: undefined
} as Partial<IGroupBy>;

export const DEFAULT_MOSAIC_CHART_TYPE_PARAM_X_PROPERTIES = {
  property: "Dates.Vintage_Year",
  title: "Vintage (Year)",
  dataType: "Integer",
  canBin: true,
  bin: null,
  performanceDateRange: undefined,
  // Declared so createMergeWithDefaults keeps it — getAllowedKeys builds its
  forecastFolderExplicit: undefined,
  // Declared for the same reason: the forecast toggle parks the axis qualification here while
  // forecast is off, and an undeclared key does not survive a restore or a chart-type switch.
  forecastPropertySuffix: undefined
} as Partial<IGroupBy>;

export const DEFAULT_MIDSTREAM_CHART_TYPE_PARAM_X_PROPERTIES = {
  property: "Header.OperatorStartDate_Year",
  title: "Operator Start (Year)",
  dataType: "Integer",
  defaultUnit: "Year",
  canBin: true,
  bin: null,
  performanceDateRange: undefined,
  // Declared so createMergeWithDefaults keeps it — getAllowedKeys builds its
  forecastFolderExplicit: undefined,
  // Declared for the same reason: the forecast toggle parks the axis qualification here while
  // forecast is off, and an undeclared key does not survive a restore or a chart-type switch.
  forecastPropertySuffix: undefined
} as Partial<IGroupBy>;

export const DEFAULT_MIDSTREAM_CHART_TYPE_PARAM_Y_PROPERTIES = {
  property: "Performance.GasInlet.CurrentCalRate",
  title: "Current Cal. Rate (Mcf/d)",
  dataType: "Number",
  defaultUnit: "Mcf/d",
  canBin: true,
  bin: null,
  performanceDateRange: undefined,
  // Declared so createMergeWithDefaults keeps it — getAllowedKeys builds its
  forecastFolderExplicit: undefined,
  // Declared for the same reason: the forecast toggle parks the axis qualification here while
  // forecast is off, and an undeclared key does not survive a restore or a chart-type switch.
  forecastPropertySuffix: undefined
} as Partial<IGroupBy>;

export const DEFAULT_PROBIT_CHART_TYPE_PARAM_Y_PROPERTIES = {
  id: "",
  property: "Well_Design.HzLength_m",
  title: FIELD_NAMES.HZ_LENGTH,
  dataType: "Number",
  canBin: true,
  defaultUnit: "",
  performanceDateRange: undefined,
  // Declared so createMergeWithDefaults keeps it — getAllowedKeys builds its
  forecastFolderExplicit: undefined,
  // Declared for the same reason: the forecast toggle parks the axis qualification here while
  // forecast is off, and an undeclared key does not survive a restore or a chart-type switch.
  forecastPropertySuffix: undefined
} as Partial<IGroupBy>;

export const DEFAULT_MIDSTREAM_CROSS_PLOT_CHART_TYPE_PARAM_Y_PROPERTIES = {
  group: "Performance",
  subgroup: "Calendar Time",
  subgroupOrder: 1,
  product: "Gas Inlet",
  property: "Performance.GasInlet.CurrentCalRate",
  title: "Current Cal. Rate (Mcf/d)",
  defaultUnit: "Mcf/d",
  dataType: "Number",
  tooltip: "",
  canBin: true,
  canNormalize: true,
  performanceDateRange: undefined,
  // Declared so createMergeWithDefaults keeps it — getAllowedKeys builds its
  forecastFolderExplicit: undefined,
  // Declared for the same reason: the forecast toggle parks the axis qualification here while
  // forecast is off, and an undeclared key does not survive a restore or a chart-type switch.
  forecastPropertySuffix: undefined
} as Partial<IGroupBy>;

export const DEFAULT_EMPTY_CHART_TYPE_PARAM_PROPERTIES = {
  canBin: false,
  dataType: "Text",
  defaultUnit: "",
  group: "",
  pdenSource: "100",
  product: "",
  property: "",
  subgroup: "",
  title: "",
  // Declared so createMergeWithDefaults keeps it — getAllowedKeys builds its
  forecastFolderExplicit: undefined,
  // Declared for the same reason: the forecast toggle parks the axis qualification here while
  // forecast is off, and an undeclared key does not survive a restore or a chart-type switch.
  forecastPropertySuffix: undefined
} as Partial<IGroupBy>;

export const DEFAULT_X_CHART_TYPE_PARAM = {
  group: "",
  subgroup: "",
  product: "",
  property: "Dates.Vintage_Year",
  title: "Vintage",
  defaultUnit: "",
  dataType: "Integer",
  canBin: true,
  pdenSource: "100"
};

export const DEFAULT_Y_CHART_TYPE_PARAM = {
  group: "",
  subgroup: "",
  product: "",
  property: "Well_Design.HzLength_m",
  title: FIELD_NAMES.HZ_LENGTH,
  defaultUnit: "",
  dataType: "Number",
  canBin: true,
  pdenSource: "100"
};

export const DEFAULT_Y_SYSTEM_CHART_TYPE_PARAM = {
  group: "",
  subgroup: "",
  product: "",
  property: "Well_Design.HzLength_m",
  title: "HzLengthEstimate",
  defaultUnit: "",
  dataType: "Number",
  canBin: true,
  pdenSource: "100"
};

export const DEFAULT_MIDSTREAM_TREND_DATE_CHART_TYPE_PARAM_X_PROPERTIES = {
  group: "",
  subgroup: "",
  product: "",
  property: "Header.FacilityStartDate_YearMonth",
  title: "Start Date (Year-Month)",
  dataType: "Text",
  canBin: false,
  performanceDateRange: undefined,
  // Declared so createMergeWithDefaults keeps it — getAllowedKeys builds its
  forecastFolderExplicit: undefined,
  // Declared for the same reason: the forecast toggle parks the axis qualification here while
  // forecast is off, and an undeclared key does not survive a restore or a chart-type switch.
  forecastPropertySuffix: undefined
} as Partial<IGroupBy>;

export const DEFAULT_MIDSTREAM_TREND_DATE_CHART_TYPE_PARAM_Y_PROPERTIES = {
  group: "Performance",
  subgroup: "Calendar Time",
  subgroupOrder: 1,
  product: "Gas Inlet",
  property: "Performance.GasInlet.CurrentCalRate",
  title: "Current Cal. Rate (Mcf/d)",
  defaultUnit: "Mcf/d",
  dataType: "Number",
  tooltip: "",
  canBin: true,
  canNormalize: true,
  performanceDateRange: undefined,
  // Declared so createMergeWithDefaults keeps it — getAllowedKeys builds its
  forecastFolderExplicit: undefined,
  // Declared for the same reason: the forecast toggle parks the axis qualification here while
  // forecast is off, and an undeclared key does not survive a restore or a chart-type switch.
  forecastPropertySuffix: undefined
} as Partial<IGroupBy>;
